import React, {useState, useCallback, useEffect, useRef} from 'react';
import PropTypes from "prop-types";
import {useSelector, useDispatch, useStore} from "react-redux";
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import {getQuestionnaireTemplate} from "../../../actions/questionnaireTemplateActions";
import {
    initializeQuestionnaireEditorUpdate,
    initializeQuestionnaireEditorCreate,
    clearQuestionnaireEditor,
    updateQuestionnaireTemplate,
    validateQuestionnaireNameAndQualifierUnique,
    validateQuestionnaireNameAndQualifierUniqueImmediately,
    addQuestionnaireCategory,
    incrementPortalCount
} from "../../../actions/questionnaireEditorActions";
import {createCategoryTemplate, getCategoryTemplate, saveCategoryTemplate} from "../../../actions/categoryTemplateActions";
import {saveGroupTemplate} from "../../../actions/groupTemplateActions";
import {saveQuestionTemplate} from "../../../actions/questionTemplateActions";
import {expanderClear} from "../../../actions/expanderActions";
import {addDrawerNameToStack, removeDrawerNameToStack} from "../../../actions/drawerStackAction";
import CategoryViewerExpandableHook from "../categories/CategoryViewerExpandableHook";
import ArgoReviewTemplateHook from "../../common/ArgoReviewTemplateHook";
import CategoryEditorHook from "../categories/CategoryEditorHook";
import GroupEditor from "../groups/GroupEditorHook";
import QuestionEditor from "../questions/QuestionEditorHook";
import ArgoMetadataEditorHook from "../../common/ArgoMetadataEditorHook";
import QuestionnaireReviewHook from "./QuestionnaireReviewHook";
import ArgoSelectPortalTextDisplayTypeHook from "../../common/ArgoSelectPortalTextDisplayTypeHook";
import ArgoTextDisplayCustomHook from "../../common/ArgoTextDisplayCustomHook";
import ArgoTextFieldHook from "../../common/ArgoTextFieldHook";
import ArgoTextFieldSelectHook from "../../common/ArgoTextFieldSelectHook";
import ArgoTextEntryWithCustomOverrideHook from "../../common/ArgoTextEntryWithCustomOverrideHook";
import * as validate from "../../../utilities/validate";
import * as metadata from "../../../utilities/metadata";
import * as expander from "../../../utilities/expander";
import * as metadataUtils from "../../../utilities/metadata";
import {compressImage, imageDataUri} from "../../../utilities/imageCompression";
import {getMetadataDefinitions} from "../../../actions/metadataDefinitionActions";
import ArgoSelectExistingHook from "../../common/ArgoSelectExistingHook";
import ArgoToolbarHook from "../../common/ArgoToolbarHook";
import ArgoPortalPreviewHook from "../../common/ArgoPortalPreviewHook";
import * as metadataTemplateTypes from "../../../constants/metadataTemplateTypes"
import * as TEMPLATE from "../../../constants/templateConstants";
import * as portalShared from "../../../portal/shared/metadataConstantsAndUtilities"
import ArgoCheckboxHook from "../../common/ArgoCheckboxHook";
import ArgoColorInputHook from "../../common/ArgoColorInputHook";
import {notificationOpen} from "../../../actions/notificationActions";
import {templateSaveSucceeded} from "../../../actions/templateSaveActions";
import useDeferredTemplateSave from "../../../hooks/useDeferredTemplateSave";
import {
    applyQuestionnairePageTextUpdates,
    discardQuestionnairePageTextUpdate,
    queueQuestionnairePageTextUpdate
} from "../../../utilities/questionnairePageTextUpdates";
import {
    createQuestionnaireSaveCancellationToken,
    validateAndSaveQuestionnaire
} from "../../../utilities/questionnaireSaveValidation";
import {createEditorOrigin, EDITOR_TYPES} from "../../../utilities/editorInstanceOwnership";



const arrayNoOrYes = [
    {value: "no", text: "No"},
    {value: "yes", text: "Yes"}
];

const arrayDefaultOrCustom = [
    {value: "default", text: "Default"},
    {value: "custom", text: "Custom"}
];

const arrayNoneOrTextOrImageOrPageNum = [
    {value: "none", text: "None"},
    {value: "text", text: "Text"},
    {value: "image", text: "Image"},
    {value: "pageNum", text: "Page X"},
    {value: "pageofNum", text: "Page X of Y"}
];

const arrayButtonTypes = [
    {value: "text", text: "Text"},
    {value: "outlined", text: "Outlined"},
    {value: "contained", text: "Contained"}
];

const arrayQuestionSubNumberFormats = [
    {value: "none", text: "None"},
    {value: "a", text: "a"},
    {value: "a.", text: "a."},
    {value: "a)", text: "a)"},
    {value: "(a)", text: "(a)"},
    {value: "#a", text: "#a"},
    {value: "#a.", text: "#a."},
    {value: "#a)", text: "#a)"},
    {value: "#(a)", text: "#(a)"},
    {value: "A", text: "A"},
    {value: "A.", text: "A."},
    {value: "A)", text: "A)"},
    {value: "(A)", text: "(A)"},
    {value: "#A", text: "#A"},
    {value: "#A.", text: "#A."},
    {value: "#A)", text: "#A)"},
    {value: "#(A)", text: "#(A)"},
    {value: "#.#", text: "#.#"}
];

const tabLabels = {
    appHeader: ["HEADER (No)", "HEADER (Yes)"],
    startPage: ["START PAGE (No)", "START PAGE (Yes)"],
    mainPages: ["MAIN PAGES (Default)", "MAIN PAGES (Custom)"],
    closePage: ["CLOSE PAGE (No)", "CLOSE PAGE (Yes)"],
    system: ["SYSTEM (Default)", "SYSTEM (Custom)"],
}

const TITLE_TYPE_STATE_OBJ = 0;
const TITLE_TYPE_TEXT_ENTRY_TYPE = 1;
const TITLE_TYPE_METADATA_OBJ_TAG = 2;
const TITLE_TYPE_METADATA_IMAGE_TAG = 3;

const textEntryTypeAppHeader = TEMPLATE.TEXT_TYPE.QUESTIONNAIRE.APP_HEADER;
const textEntryTypePageHeader = TEMPLATE.TEXT_TYPE.QUESTIONNAIRE.APP_HEADER;
const textEntryTypePageFooter1 = TEMPLATE.TEXT_TYPE.QUESTIONNAIRE.APP_HEADER;
const textEntryTypePageFooter2 = TEMPLATE.TEXT_TYPE.QUESTIONNAIRE.APP_HEADER;

const appHeaderTag = portalShared.CUSTOM_QNR_APP_HEADER_OBJ;
const mainPagesTag = portalShared.CUSTOM_QNR_MAIN_PAGES_OBJ;

const titleType = {
    appHeaderLeft: ["appHeaderObj", textEntryTypeAppHeader, appHeaderTag, portalShared.HEADER_APPBAR_IMAGE_LEFT],
    appHeaderCenter: ["appHeaderObj", textEntryTypeAppHeader, appHeaderTag, portalShared.HEADER_APPBAR_IMAGE_CENTER],
    appHeaderRight: ["appHeaderObj", textEntryTypeAppHeader, appHeaderTag, portalShared.HEADER_APPBAR_IMAGE_RIGHT],
    pageHeaderLeft: ["mainPagesObj", textEntryTypePageHeader, mainPagesTag, portalShared.PAGE_HEADER_IMAGE_LEFT],
    pageHeaderCenter: ["mainPagesObj", textEntryTypePageHeader, mainPagesTag, portalShared.PAGE_HEADER_IMAGE_CENTER],
    pageHeaderRight: ["mainPagesObj", textEntryTypePageHeader, mainPagesTag, portalShared.PAGE_HEADER_IMAGE_RIGHT],
    pageFooter1Left: ["mainPagesObj", textEntryTypePageFooter1, mainPagesTag, portalShared.PAGE_FOOTER1_IMAGE_LEFT],
    pageFooter1Center: ["mainPagesObj", textEntryTypePageFooter1, mainPagesTag, portalShared.PAGE_FOOTER1_IMAGE_CENTER],
    pageFooter1Right: ["mainPagesObj", textEntryTypePageFooter1, mainPagesTag, portalShared.PAGE_FOOTER1_IMAGE_RIGHT],
    pageFooter2Left: ["mainPagesObj", textEntryTypePageFooter2, mainPagesTag, portalShared.PAGE_FOOTER2_IMAGE_LEFT],
    pageFooter2Center: ["mainPagesObj", textEntryTypePageFooter2, mainPagesTag, portalShared.PAGE_FOOTER2_IMAGE_CENTER],
    pageFooter2Right: ["mainPagesObj", textEntryTypePageFooter2, mainPagesTag, portalShared.PAGE_FOOTER2_IMAGE_RIGHT],
}

const useQuestionSubNumbers = false; // For future considerations

const QUESTION_DEPTH = 0;

// NOTE: LEGACY there used to be 3 types of questionnaires, SIMPLE, COMPLEX and DYNAMIC
// SIMPLE allowed questions to be added directly to a questionnaire without categories and groups
// DYNAMIC was never implemented
// COMPLEX is now the only supported format

const QuestionnaireEditor = (props) => {
    // Destructure props
    // const {
    //     propsCreateMode,
    //     cloneMode,
    //     propsTemplateKey,
    //     propsTemplateId,
    //     propsHandleToolbarButtonClick,
    //     propswidth,
    //     filter,
    //     questionKeys
    // } = props;

    const dispatch = useDispatch();
    const store = useStore();

    // Redux selectors
    const options = useSelector(state => state.questionnaireEditor.options);
    const template = useSelector(state => state.questionnaireEditor.template);
    const templateEditor = useSelector(state => state.questionnaireEditor.template);
    const templateOriginal = useSelector(state => state.questionnaireTemplate[props.templateKey]);
    const questionnaireTemplate = useSelector(state => state.questionnaireTemplate);
    const categoryKeys = useSelector(state => state.questionnaireEditor.categoryKeys);
    const categoryTemplate = useSelector(state => state.categoryTemplate);
    const metadataDefinition = useSelector(state => state.metadataDefinition[metadataTemplateTypes.QuestionnaireTemplate]);

    // Initial state
    const getInitialState = () => ({
        expander: {},
        expandAll: true,
        isLoading: false,
        toolbarErrorText: "",
        isThisAnExistingCategory: "0",
        categoryTemplateSearch: "",
        openCategoryReview: false,
        showCategoryBackButton: true,
        selectedCategoryInfoKey: "",
        openGroupReview: false,
        showGroupBackButton: true,
        selectedGroupInfoKey: "",
        openQuestionReview: false,
        showQuestionBackButton: true,
        selectedQuestionInfoKey: "",
        selectedQuestionInfoId: "",
        openCategoryEditor: false,
        categoryTemplate: {},
        openGroupEditor: false,
        groupTemplate: {},
        openGroupEditorComplete: false,
        openQuestionEditor: false,
        questionTemplate: {},
        nameErrorText: " ",
        metadataSet: false,
        metadataErrorText: "",
        nameDisplay: metadata.getDefaultMetadataObj(),
        descDisplay: metadata.getDefaultMetadataObj(),
        portalTabTitle: "",
        keyMetricCategories: [],
        keyMetricCategory: 0,
        selectedTabNumber: 0,

        tab0: tabLabels.appHeader[0],
        appHeader: "no",
        appHeaderObj: null,

        tab1: tabLabels.startPage[0],
        startPage: "no",
        hideNameOnStartPage: false,
        startPageObj: null,
        startPageScript: "",
        startPageScriptDisplay: metadata.getDefaultMetadataObj(),
        startPageText: portalShared.customQuestionnaireStartPageObj.defaultText,
        startPageTextDisplay: metadata.getDefaultMetadataObj(),

        tab2: tabLabels.mainPages[0],
        mainPages: "default",
        mainPagesObj: null,

        tab3: tabLabels.closePage[1],
        closePage: "yes",
        noGreenCheck: false,
        closePageObj: portalShared.getCustomQuestionnaireClosePageObj(),
        closePageScript: "",
        closePageScriptDisplay: metadata.getDefaultMetadataObj(),
        closePageText: portalShared.customQuestionnaireClosePageObj.defaultText,
        closePageTextDisplay: metadata.getDefaultMetadataObj(),

        tab4: tabLabels.system[0],
        system: "default",
        systemObj: portalShared.getCustomQuestionnaireSystemObj(),

        dummyDisplay: metadata.getDefaultMetadataObj(),

        cloneInit: false
    });

    const [state, setStateInternal] = useState(getInitialState);
    
    const saveCancellationTokenRef = useRef(null);
    if (saveCancellationTokenRef.current === null) {
        saveCancellationTokenRef.current = createQuestionnaireSaveCancellationToken();
    }

    // Debounce timer for metadata text changes
    const metadataDebounceRef = useRef(null);
    const pendingMetadataUpdateRef = useRef(null);

    // Debounce timer for template text changes (name, description, nameQualifier)
    const templateDebounceRef = useRef(null);
    const pendingTemplateUpdateRef = useRef(null);

    // Debounce timer for start/close page default text (separate from metadata)
    const pageTextDebounceRef = useRef(null);
    const pendingPageTextUpdateRef = useRef(null);

    // Local shadow of template text fields for immediate display during debounce
    const [localTemplateFields, setLocalTemplateFields] = useState({});

    // Helper function to mimic class setState behavior
    const setState = (updates, callback) => {
        setStateInternal(prevState => {
            const newState = typeof updates === 'function' ? updates(prevState) : { ...prevState, ...updates };
            if (callback) {
                setTimeout(callback, 0);
            }
            return newState;
        });
    };

    // componentDidMount equivalent
    useEffect(() => {
        if (saveCancellationTokenRef.current.isCancelled()) {
            saveCancellationTokenRef.current = createQuestionnaireSaveCancellationToken();
        }
        const editorCancellationToken = saveCancellationTokenRef.current;

        dispatch(getMetadataDefinitions(metadataTemplateTypes.QuestionnaireTemplate));

        dispatch(addDrawerNameToStack(props.createMode ? "Create" : "Edit", "Questionnaire"))

        if (props.templateKey || props.templateId) {
            if (questionnaireTemplate[props.templateKey] &&
                (questionnaireTemplate[props.templateKey].id === props.templateId)) {
                dispatch(initializeQuestionnaireEditorUpdate(questionnaireTemplate[props.templateKey]));
            }
            else if (props.templateKey) {
                setState({isLoading: true});
                dispatch(getQuestionnaireTemplate(props.templateKey, props.templateId));
            }

            if (templateOriginal && templateOriginal.metadata) {
                pageInit(props, state.keyMetricCategories);
            }
        }
        else {
            dispatch(initializeQuestionnaireEditorCreate());
        }

        if (templateOriginal && templateOriginal.metadata) {
            pageInit(props, state.keyMetricCategories);
        }

        // Cleanup on unmount
        return () => {
            editorCancellationToken.cancel();
            clearTimeout(metadataDebounceRef.current);
            clearTimeout(templateDebounceRef.current);
            clearTimeout(pageTextDebounceRef.current);
            dispatch(expanderClear("QuestionnaireEditor"));
            dispatch(clearQuestionnaireEditor());
            dispatch(removeDrawerNameToStack())
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // componentWillReceiveProps equivalent (keeps UI state aligned with class behavior)
    useEffect(() => {

        let tempKeyMetricCategories = [];
        const keyMetricCategoriesSafe = Array.isArray(state.keyMetricCategories)
            ? state.keyMetricCategories
            : [];

        if (!state.metadataSet) {
            if (keyMetricCategoriesSafe.length === 0) {
                if (Array.isArray(metadataDefinition)) {
                    tempKeyMetricCategories.push("None");
                    for (let i = 0; i < metadataDefinition.length; i++) {
                        if (metadataDefinition[i].name === portalShared.KEY_METRIC_CATEGORY_QUESTIONNAIRE) {
                            const listValues = Array.isArray(metadataDefinition[i].listValues)
                                ? metadataDefinition[i].listValues
                                : [];
                            for (let j = 0; j < listValues.length; j++) {
                                tempKeyMetricCategories.push(listValues[j]);
                            }
                        }
                    }
                    setState({keyMetricCategories: tempKeyMetricCategories});
                }
            } else {
                tempKeyMetricCategories = keyMetricCategoriesSafe;
            }
        }

        const isTemplateLoaded = (templateOriginal && templateOriginal.templateKey === props.templateKey
            && template.templateKey !== props.templateKey);
        const editTemplateReady = template.id !== props.templateId &&
            (questionnaireTemplate[props.templateKey] && questionnaireTemplate[props.templateKey].id === props.templateId);

        if (props.cloneMode && !state.cloneInit && template.name !== "") {
            setState({cloneInit: true});
            dispatch(validateQuestionnaireNameAndQualifierUnique(template.name, template.nameQualifier));
        }

        if (isTemplateLoaded && editTemplateReady && (tempKeyMetricCategories.length > 0)) {
            dispatch(initializeQuestionnaireEditorUpdate(templateOriginal));

            let tempProps = {
                template: template,
                templateEditor: templateEditor,
                templateOriginal: templateOriginal,
                questionnaireTemplate: questionnaireTemplate,
                ...props
            };

            pageInit(tempProps, tempKeyMetricCategories);
            return;
        }

        if (!state.metadataSet) {
            const categoryKeysSafe = Array.isArray(categoryKeys) ? categoryKeys : [];
            setState({
                expander: expander.presetExpander(categoryKeysSafe, state.expander),
                keyMetricCategories: tempKeyMetricCategories.length ? tempKeyMetricCategories : keyMetricCategoriesSafe,
                isLoading: (isTemplateLoaded && editTemplateReady)
            });
        }

        checkForErrorsOnScreen();
        dispatch(incrementPortalCount());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        templateOriginal,
        metadataDefinition,
        template,
        questionnaireTemplate,
        categoryKeys,
        props.cloneMode,
        props.templateKey,
        props.templateId
    ]);

    useEffect(() => {

        if (categoryKeys.length) {
            dispatch(getCategoryTemplate(categoryKeys[0]));
        }

    },[categoryKeys, categoryTemplate, dispatch]);

    const handleToolbarButtonClick = (event, fieldName, buttonTitle) => {
        switch (buttonTitle) {
            case "BACK":
                setState({openCategoryReview: false, openGroupReview: false, openQuestionReview: false});
                break;
            // no default
        }
    }

    const saveQuestionnaire = (event, fieldName) => {
        return validateAndSaveQuestionnaire({
            flushPendingUpdates: () => flushPendingUpdates(false),
            getEditorState: () => store.getState().questionnaireEditor,
            validateNameAndQualifier: (name, nameQualifier) => dispatch(
                validateQuestionnaireNameAndQualifierUniqueImmediately(name, nameQualifier)
            ),
            save: (latestTemplate, latestOptions) => props.handleToolbarButtonClick(
                event,
                fieldName,
                "SAVE",
                latestTemplate,
                latestOptions
            ),
            cloneMode: props.cloneMode,
            onInvalid: () => dispatch(notificationOpen("Validate fields before saving.")),
            cancellationToken: saveCancellationTokenRef.current
        });
    };

    const notifyDeferredSave = () => {
        dispatch(notificationOpen("Waiting for the new template to finish saving…"));
    };
    const saveQuestionnaireWhenReady = useDeferredTemplateSave(saveQuestionnaire, notifyDeferredSave);

    const handleToolbarButtonClickWithFlush = (event, fieldName, buttonTitle, templateEditorArg, toolbarOptions) => {
        if (buttonTitle === "SAVE") {
            return saveQuestionnaireWhenReady(event, fieldName);
        }
        return props.handleToolbarButtonClick(event, fieldName, buttonTitle, templateEditorArg, toolbarOptions);
    }

    // Allow child components to set errors on this page.
    const setStateCallback = (stateObj, saveAfter = false, templateType) => {

        setState(stateObj);
        if ((templateType === TEMPLATE.TYPE.CATEGORY) && saveAfter) {
            return categoryEditorSave(stateObj.categoryTemplate);
        }
        if ((templateType === TEMPLATE.TYPE.GROUP) && saveAfter) {
            return groupEditorSave(stateObj.groupTemplate);
        }
        if ((templateType === TEMPLATE.TYPE.QUESTION) && saveAfter) {
            return questionEditorSave(stateObj.questionTemplate);
        }
        return undefined;
    }

    const pageInit = (currProps, keyMetricCategories) => {

        const keyMetricCategoriesSafe = Array.isArray(keyMetricCategories)
            ? keyMetricCategories
            : [];

        let metadataObj = {};

        if (currProps.hasOwnProperty("templateOriginal")) {
            metadataObj = currProps.templateOriginal.hasOwnProperty("metadata") ? currProps.templateOriginal.metadata : {};
        }

        let tempNameDisplay = metadata.setTextObjSelectedAndValueAndValueObj(metadataObj, portalShared.CUSTOM_DDO_NAME);
        let tempDescDisplay = metadata.setTextObjSelectedAndValueAndValueObj(metadataObj, portalShared.CUSTOM_DDO_DESC);

        // -------------------------------------------------------------------------------------------------------------
        // ---------- PORTAL TAB TITLE
        // -------------------------------------------------------------------------------------------------------------

        let tempPortalTabTitle = "";

        if (metadataObj.hasOwnProperty(portalShared.PORTAL_TAB_TITLE)) {
            tempPortalTabTitle = metadataObj[portalShared.PORTAL_TAB_TITLE];
        }

        // -------------------------------------------------------------------------------------------------------------
        // ---------- Questionnaire Classification AKA REPORTING CATEGORY
        // -------------------------------------------------------------------------------------------------------------

        let tempKeyMetricCategory = "0";

        // LEGACY was named REPORTING CATEGORY
        if (metadataObj.hasOwnProperty(portalShared.KEY_METRIC_CATEGORY_QUESTIONNAIRE)) {

            let tempKeyMetricCategoryText = metadataObj[portalShared.KEY_METRIC_CATEGORY_QUESTIONNAIRE];
            for (let idx = 0; idx < keyMetricCategoriesSafe.length; idx++) {
                if (keyMetricCategoriesSafe[idx] === tempKeyMetricCategoryText) {
                    tempKeyMetricCategory = idx;
                    break;
                }
            }
        }

        // -------------------------------------------------------------------------------------------------------------
        // ---------- APP BAR HEADER INIT
        // -------------------------------------------------------------------------------------------------------------

        let tempTab0 = tabLabels.appHeader[0];
        let tempAppHeader = "no";
        let tempAppHeaderObj = null;

        if (metadataObj.hasOwnProperty(portalShared.CUSTOM_QNR_APP_HEADER_OBJ)) {
            tempAppHeader = "yes";
            tempTab0 = tabLabels.appHeader[1];
            tempAppHeaderObj = JSON.parse(metadataObj[portalShared.CUSTOM_QNR_APP_HEADER_OBJ]); // PORTAL will never display appBar
        }

        // -------------------------------------------------------------------------------------------------------------
        // ---------- START PAGE INIT
        // -------------------------------------------------------------------------------------------------------------

        let tempTab1 = tabLabels.startPage[0];
        let tempHideName = false;
        let tempStartPage = "no";
        let tempStartPageObj = portalShared.getCustomQuestionnaireStartPageObj();
        let tempStartPageScript = "";
        let tempStartPageScriptDisplay = metadata.getDefaultMetadataObj();
        let tempStartPageText = portalShared.customQuestionnaireStartPageObj.defaultText;

        let tempStartPageTextDisplay = metadata.setTextObjSelectedAndValueAndValueObj(metadataObj, portalShared.CUSTOM_QNR_START_PAGE_OBJ);

        // LEGACY user-script is used as custom text on the Start Page
        //let tempStartPageScriptDisplayObj = metadata.setTextObjSelectedAndValueAndValueObj(metadataObj, portalShared.QNR_START_PAGE_SCRIPT_FORMAT_OBJ);

        if (metadataObj.hasOwnProperty(portalShared.CUSTOM_QNR_START_PAGE_OBJ)) {
            tempStartPage = "yes";
            tempTab1 = tabLabels.startPage[1];

            tempStartPageObj = JSON.parse(metadataObj[portalShared.CUSTOM_QNR_START_PAGE_OBJ]);

            tempStartPageText = tempStartPageObj.defaultText;
            tempHideName = tempStartPageObj.hideName;

            if (tempStartPageObj.defaultTextCustomObj) {
                tempStartPageTextDisplay.metadataValue = JSON.stringify(tempStartPageObj.defaultTextCustomObj);
                tempStartPageTextDisplay.metadataValueObj = tempStartPageObj.defaultTextCustomObj;
                tempStartPageTextDisplay.selected = "custom"; // Must be custom since object exists
            } else {
                tempStartPageTextDisplay.metadataValue = "";
                tempStartPageTextDisplay.metadataValueObj = {};
                tempStartPageTextDisplay.selected = "default";
            }

        }

        if (metadataObj.hasOwnProperty(portalShared.QNR_START_PAGE_SCRIPT)) {
            tempStartPageScript = metadataObj[portalShared.QNR_START_PAGE_SCRIPT];
        }

        if (metadataObj.hasOwnProperty(portalShared.QNR_START_PAGE_SCRIPT_FORMAT_OBJ)) {
            tempStartPageScriptDisplay.selected = "custom";
            tempStartPageScriptDisplay.metadataValue = metadataObj[portalShared.QNR_START_PAGE_SCRIPT_FORMAT_OBJ];
            tempStartPageScriptDisplay.metadataValueObj = JSON.parse(tempStartPageScriptDisplay.metadataValue);
        }

        // -------------------------------------------------------------------------------------------------------------
        // ---------- MAIN PAGES INIT
        // -------------------------------------------------------------------------------------------------------------

        let tempTab2 = tabLabels.mainPages[0];
        let tempMainPages = "default";
        let tempMainPagesObj = null;

        if (metadataObj.hasOwnProperty(portalShared.CUSTOM_QNR_MAIN_PAGES_OBJ)) {
            tempMainPages = "custom";
            tempTab2 = tabLabels.mainPages[1];
            tempMainPagesObj = JSON.parse(metadataObj[portalShared.CUSTOM_QNR_MAIN_PAGES_OBJ]);
        }

        // -------------------------------------------------------------------------------------------------------------
        // ---------- CLOSE PAGE INIT
        // -------------------------------------------------------------------------------------------------------------

        let tempTab3 = tabLabels.closePage[0];
        let tempNoGreenCheck = false;
        let tempClosePage = "no";
        let tempClosePageObj = portalShared.getCustomQuestionnaireClosePageObj();
        let tempClosePageScript = "";
        let tempClosePageScriptDisplay = metadata.getDefaultMetadataObj();
        let tempClosePageText = portalShared.customQuestionnaireClosePageObj.defaultText;
        let tempClosePageTextDisplay = metadata.setTextObjSelectedAndValueAndValueObj(metadataObj, portalShared.CUSTOM_QNR_CLOSE_PAGE_OBJ);

        if (metadataObj.hasOwnProperty(portalShared.CUSTOM_QNR_CLOSE_PAGE_OBJ)) {
            tempClosePage = "yes";
            tempTab3 = tabLabels.closePage[1];

            tempClosePageObj = JSON.parse(metadataObj[portalShared.CUSTOM_QNR_CLOSE_PAGE_OBJ]);

            tempNoGreenCheck = tempClosePageObj.noGreenCheck;

            tempClosePageText = tempClosePageObj.defaultText;

            if (tempClosePageObj.defaultTextCustomObj) {
                tempClosePageTextDisplay.metadataValue = JSON.stringify(tempClosePageObj.defaultTextCustomObj);
                tempClosePageTextDisplay.metadataValueObj = tempClosePageObj.defaultTextCustomObj;
                tempClosePageTextDisplay.selected = "custom"; // Must be custom since object exists
            } else {
                tempClosePageTextDisplay.metadataValue = "";
                tempClosePageTextDisplay.metadataValueObj = {};
                tempClosePageTextDisplay.selected = "default";
            }

        }

        // LEGACY user-script-bottom is used as custom text on the Close Page
        if (metadataObj.hasOwnProperty(portalShared.QNR_CLOSE_PAGE_SCRIPT)) {
            tempClosePageScript = metadataObj[portalShared.QNR_CLOSE_PAGE_SCRIPT];
        }

        if (metadataObj.hasOwnProperty(portalShared.QNR_CLOSE_PAGE_SCRIPT_FORMAT_OBJ)) {
            tempClosePageScriptDisplay.selected = "custom";
            tempClosePageScriptDisplay.metadataValue = metadataObj[portalShared.QNR_CLOSE_PAGE_SCRIPT_FORMAT_OBJ];
            tempClosePageScriptDisplay.metadataValueObj = JSON.parse(tempClosePageScriptDisplay.metadataValue);
        }

        // -------------------------------------------------------------------------------------------------------------
        // ---------- SYSTEM INIT
        // -------------------------------------------------------------------------------------------------------------

        let tempTab4 = tabLabels.system[0];
        let tempSystem = "default";
        let tempSystemObj = portalShared.getCustomQuestionnaireSystemObj();

        if (metadataObj.hasOwnProperty(portalShared.CUSTOM_QNR_SYSTEM_OBJ)) {
            tempSystem = "custom";
            tempTab4 = tabLabels.system[1];
            tempSystemObj = JSON.parse(metadataObj[portalShared.CUSTOM_QNR_SYSTEM_OBJ]);
        }

        // -------------------------------------------------------------------------------------------------------------
        // ---------- SET STATE
        // -------------------------------------------------------------------------------------------------------------

        setState({
            nameDisplay: tempNameDisplay,
            descDisplay: tempDescDisplay,
            portalTabTitle: tempPortalTabTitle,
            keyMetricCategory: tempKeyMetricCategory,

            tab0: tempTab0,
            appHeader: tempAppHeader,
            appHeaderObj: tempAppHeaderObj,

            tab1: tempTab1,
            hideNameOnStartPage: tempHideName,
            startPage: tempStartPage,
            startPageObj: tempStartPageObj,
            startPageScript: tempStartPageScript,
            startPageScriptDisplay: tempStartPageScriptDisplay,
            startPageText: tempStartPageText,
            startPageTextDisplay: tempStartPageTextDisplay,

            tab2: tempTab2,
            mainPages: tempMainPages,
            mainPagesObj: tempMainPagesObj,

            tab3: tempTab3,
            noGreenCheck: tempNoGreenCheck,
            closePage: tempClosePage,
            closePageObj: tempClosePageObj,
            closePageScript: tempClosePageScript,
            closePageScriptDisplay: tempClosePageScriptDisplay,
            closePageText: tempClosePageText,
            closePageTextDisplay: tempClosePageTextDisplay,

            tab4: tempTab4,
            system: tempSystem,
            systemObj: tempSystemObj,
        });

    };

    const checkForErrorsOnScreen = useCallback(() => {
        const currentProps = { template, options, templateKey: props.templateKey, templateId: props.templateId };
        let nameErrorText = validate.templateNameAndQualifierUnique(currentProps, "Questionnaire Name", props.cloneMode);
        let metadataErrorText = state.metadataErrorText;
        let toolbarErrorText = "";

        if (nameErrorText !== " ") {
            toolbarErrorText = nameErrorText;
        }

        if (metadataErrorText !== "") {
            toolbarErrorText = toolbarErrorText === "" ? state.metadataErrorText : toolbarErrorText + ",  " + state.metadataErrorText;
        }

        setStateInternal(previousState => {
            if (previousState.nameErrorText === nameErrorText
                && previousState.metadataErrorText === metadataErrorText
                && previousState.toolbarErrorText === toolbarErrorText) {
                return previousState;
            }

            return {...previousState, nameErrorText, metadataErrorText, toolbarErrorText};
        });
    }, [options, props.cloneMode, props.templateId, props.templateKey, state.metadataErrorText, template]);

    useEffect(() => {
        checkForErrorsOnScreen()
    }, [checkForErrorsOnScreen]);

    const onTextChangePortalTabTitle = (event) => {
        setState({"portalTabTitle": event.target.value});
        handleChangeMetadataValueDebounced(portalShared.PORTAL_TAB_TITLE, event.target.value);
    }


    const onSelectChangeKeyMetricCategory = (event, fieldName) => {
        let index = event.target.value;
        let selected = state.keyMetricCategories[index];  // Note: "None: has been added to the metadata list state.keyMetricCategories, so metadata needs to adjust

        setState({[fieldName]: index}, () => handleChangeMetadataValue(portalShared.KEY_METRIC_CATEGORY_QUESTIONNAIRE, selected));
    };


    const handleChangeMetadataValue = (fieldName, value, removeMetadataTab = null) => {

        let updatedTemplate = {...store.getState().questionnaireEditor.template};
        let metadataCopy = {...updatedTemplate.metadata};  // Copy metadata from template
        metadataCopy[fieldName] = value; // Assign new value to metadata

        // In some cases when a value changes a related metadata entry needs to be removed
        // If an array of values is passed then remove all related metadata entries
        if (removeMetadataTab !== null) {
            if (Array.isArray(removeMetadataTab)) {
                for (let i = 0; i < removeMetadataTab.length; i++) {
                    if (metadataCopy.hasOwnProperty(removeMetadataTab[i])) {
                        delete metadataCopy[removeMetadataTab[i]];
                    }
                }
            }
            else if (metadataCopy.hasOwnProperty(removeMetadataTab)) {
                delete metadataCopy[removeMetadataTab];
            }
        }

        updatedTemplate.metadata = metadataCopy; // Update template with new metadata
        dispatch(updateQuestionnaireTemplate(updatedTemplate)); // Update questionnaire with new metadata
    };

    const applyPendingMetadataUpdate = () => {
        const pendingUpdate = pendingMetadataUpdateRef.current;
        if (!pendingUpdate) {
            return;
        }

        clearTimeout(metadataDebounceRef.current);
        pendingMetadataUpdateRef.current = null;
        metadataDebounceRef.current = null;

        if (pendingUpdate.metadataPatch) {
            const currentTemplate = store.getState().questionnaireEditor.template;
            const metadataCopy = {...currentTemplate.metadata, ...pendingUpdate.metadataPatch};
            pendingUpdate.removedMetadataKeys.forEach(key => delete metadataCopy[key]);
            dispatch(updateQuestionnaireTemplate({...currentTemplate, metadata: metadataCopy}));
            return;
        }

        const currentTemplate = store.getState().questionnaireEditor.template;
        const metadataCopy = {...currentTemplate.metadata};
        Object.values(pendingUpdate.valueUpdates).forEach(({fieldName, value, removeMetadataTab}) => {
            metadataCopy[fieldName] = value;
            const keysToRemove = Array.isArray(removeMetadataTab)
                ? removeMetadataTab
                : [removeMetadataTab];
            keysToRemove
                .filter(key => key !== null)
                .forEach(key => delete metadataCopy[key]);
        });
        dispatch(updateQuestionnaireTemplate({...currentTemplate, metadata: metadataCopy}));
    };

    const handleChangeMetadataValueDebounced = (fieldName, value, removeMetadataTab = null) => {
        if (pendingMetadataUpdateRef.current?.metadataPatch) {
            applyPendingMetadataUpdate();
        }
        clearTimeout(metadataDebounceRef.current);
        const pendingUpdate = pendingMetadataUpdateRef.current ?? {valueUpdates: {}};
        pendingMetadataUpdateRef.current = {
            valueUpdates: {
                ...pendingUpdate.valueUpdates,
                [fieldName]: {fieldName, value, removeMetadataTab}
            }
        };
        metadataDebounceRef.current = setTimeout(applyPendingMetadataUpdate, 300);
    };

    const applyPendingTemplateUpdate = (validateName = true) => {
        const pendingUpdate = pendingTemplateUpdateRef.current;
        if (!pendingUpdate) {
            return;
        }

        clearTimeout(templateDebounceRef.current);
        pendingTemplateUpdateRef.current = null;
        templateDebounceRef.current = null;

        const currentTemplate = store.getState().questionnaireEditor.template;
        const updatedTemplate = {...currentTemplate, ...pendingUpdate.values};
        dispatch(updateQuestionnaireTemplate(updatedTemplate));
        if (pendingUpdate.validateName && validateName) {
            dispatch(validateQuestionnaireNameAndQualifierUnique(
                updatedTemplate.name,
                updatedTemplate.nameQualifier
            ));
        }
    };

    const dispatchTemplateUpdateDebounced = (fieldName, value) => {
        clearTimeout(templateDebounceRef.current);
        const pendingUpdate = pendingTemplateUpdateRef.current ?? {values: {}, validateName: false};
        pendingTemplateUpdateRef.current = {
            values: {...pendingUpdate.values, [fieldName]: value},
            validateName: pendingUpdate.validateName || fieldName === "name" || fieldName === "nameQualifier"
        };
        templateDebounceRef.current = setTimeout(applyPendingTemplateUpdate, 300);
    };

    const applyPendingPageTextUpdate = () => {
        const pendingUpdates = pendingPageTextUpdateRef.current;
        if (!pendingUpdates) {
            return;
        }

        clearTimeout(pageTextDebounceRef.current);
        pendingPageTextUpdateRef.current = null;
        pageTextDebounceRef.current = null;

        const currentTemplate = store.getState().questionnaireEditor.template;
        const updatedTemplate = portalShared.cloneObj(currentTemplate);
        updatedTemplate.metadata = applyQuestionnairePageTextUpdates(
            updatedTemplate.metadata,
            pendingUpdates
        );
        dispatch(updateQuestionnaireTemplate(updatedTemplate));
    };

    const discardPendingPageTextUpdate = metadataTag => {
        pendingPageTextUpdateRef.current = discardQuestionnairePageTextUpdate(
            pendingPageTextUpdateRef.current,
            metadataTag
        );
        if (!pendingPageTextUpdateRef.current) {
            clearTimeout(pageTextDebounceRef.current);
            pageTextDebounceRef.current = null;
        }
    };

    // Flush any pending debounced updates so Save gets the latest data
    const flushPendingUpdates = (validateName = true) => {
        applyPendingTemplateUpdate(validateName);
        applyPendingMetadataUpdate();
        applyPendingPageTextUpdate();
    };

    const handleMetadataChange = (metadataObj) => {
        let updatedTemplate = {...template, metadata: metadataObj};
        checkForErrorsOnScreen();

        dispatch(updateQuestionnaireTemplate(updatedTemplate));
    };

    const setQuestionnaireCustomMetadata = (fieldNameObj, fieldName, selected, metadataTag, metadataValueObj, deleteAdditionalTagsArray = []) => {

        let updatedTemplate = {...template};
        let tempMetadata = {...updatedTemplate.metadata};

        let metadataValueStr = (typeof metadataValueObj === "string") ? metadataValueObj : JSON.stringify(metadataValueObj);


        if ((selected === "default") || (selected === "no") || (selected === "None")) {
            if (metadataTag === portalShared.CUSTOM_QNR_START_PAGE_OBJ ||
                metadataTag === portalShared.CUSTOM_QNR_CLOSE_PAGE_OBJ) {
                discardPendingPageTextUpdate(metadataTag);
            }
            if (tempMetadata.hasOwnProperty(metadataTag)) {
                delete tempMetadata[metadataTag];
            }
        } else {
            tempMetadata[metadataTag] = metadataValueStr;
        }

        for (let i = 0; i < deleteAdditionalTagsArray.length; i++) {
            delete tempMetadata[deleteAdditionalTagsArray[i]];
        }

        //let metadataSaveName = metadataTag;
        //let metadataSaveObjName = "";

        handleMetadataChange(tempMetadata);
    };


    const setCustomMetadataLayered = (templateArg, updateTemplateFunction, setStateCallbackFunction, fieldName, selected, metadataTag, metadataValueObj) => {

        let updatedTemplate = {...template};
        let tempMetadata = {...updatedTemplate.metadata};

        let tempValueObj = JSON.parse(tempMetadata[metadataTag]);
        tempValueObj[fieldName] = metadataValueObj;
        tempMetadata[metadataTag] = JSON.stringify(tempValueObj);
        //let tempName = "";

        // switch(fieldName) {
        //     case "textLeftCustomObj":
        //     case "textCenterCustomObj":
        //     case "textRightCustomObj":
        //
        //         // tempName = "appHeaderObj." + fieldName;
        //         break;
        //
        //     case "pageHeaderLeftTextObj":
        //     case "pageHeaderCenterTextObj":
        //     case "pageHeaderRightTextObj":
        //     case "pageFooter1LeftTextObj":
        //     case "pageFooter1CenterTextObj":
        //     case "pageFooter1RightTextObj":
        //     case "pageFooter2LeftTextObj":
        //     case "pageFooter2CenterTextObj":
        //     case "pageFooter2RightTextObj":
        //
        //         // tempName = "mainPagesObj." + fieldName;
        //         break;
        //
        //     // no default
        // }

        setState({tempName: metadataValueObj});

        handleMetadataChange(tempMetadata);
    };

    const setImageMetadata = (metadataTag, metadataValueStr) => {

        let updatedTemplate = {...template};
        let tempMetadata = {...updatedTemplate.metadata};


        tempMetadata[metadataTag] = metadataValueStr;


        handleMetadataChange(tempMetadata);
    };

    const setMetadataErrorText = (metadataErrorText) => {
        setState({metadataErrorText: metadataErrorText, metadataSet: false}, () => checkForErrorsOnScreen());
    }

    const metadataChanged = (updatedTemplate, depth) => {

        // Note: Timing issues was causing componentWillReceiveProps to trigger and a setState was canceling another setState
        setState({metadataSet: true});
        applyPendingMetadataUpdate();
        clearTimeout(metadataDebounceRef.current);
        const previousMetadata = template.metadata ?? {};
        const updatedMetadata = updatedTemplate.metadata ?? {};
        const metadataPatch = Object.keys(updatedMetadata).reduce((patch, key) => {
            if (updatedMetadata[key] !== previousMetadata[key]) {
                patch[key] = updatedMetadata[key];
            }
            return patch;
        }, {});
        const removedMetadataKeys = Object.keys(previousMetadata)
            .filter(key => !Object.prototype.hasOwnProperty.call(updatedMetadata, key));
        pendingMetadataUpdateRef.current = {metadataPatch, removedMetadataKeys, depth};
        metadataDebounceRef.current = setTimeout(applyPendingMetadataUpdate, 300);
    }

    const onTextChange = (event, fieldName) => {
        let value = event.target.value;

        if ((fieldName === "nameQualifier") && (value==="")) {
            value = null;
        }

        // ArgoTextFieldHook fields need local state overlay since they don't manage own display
        if (["nameQualifier", "expirationDays", "name"].includes(fieldName)) {
            setLocalTemplateFields(prev => ({...prev, [fieldName]: value}));
        }

        dispatchTemplateUpdateDebounced(fieldName, value);
    };


    // onChangeArgoAutocompleteHook = (event, fieldName) => {
    //
    //     let value = event.target.value;
    //
    //     switch(fieldName) {
    //         case "name":
    //         case "description":
    //
    //             let updatedTemplate = {...template};
    //
    //             updatedTemplate[fieldName] = value;
    //
    //             checkForErrorsOnScreen();
    //
    //             dispatch(updateQuestionnaireTemplate(updatedTemplate));
    //
    //             if (props.cloneMode) {
    //                 props.checkNameQualifier(updatedTemplate.nameQualifier, updatedTemplate.id);
    //             }
    //
    //             // if (fieldName === "name")
    //             // {
    //             //     dispatch(validateQuestionnaireNameAndQualifierUnique(updatedTemplate.name, updatedTemplate.nameQualifier));
    //             // }
    //             // else {
    //             //     props.checkQuestionnaireValue({name: updatedTemplate.name, templateKey: updatedTemplate.templateKey});
    //             // }
    //
    //             break;
    //         case portalShared.QNR_START_PAGE_SCRIPT:
    //         case portalShared.QNR_CLOSE_PAGE_SCRIPT: // Just added this 08/06/2024
    //             // NOTE: These are LEGACY text fields designed for Conversation Preview and The Foundation Application
    //             // The Foundation Application was the predecessor to Portal Application for displaying surveys
    //             // However it could only display one question per page
    //             // Conversation Preview was a module in Admin to simulate The Foundation App displaying a survey
    //             // "user-script" was text displayed on the start page
    //             // "user-script-bottom" was text displayed on the close page
    //             // These fields are stored in metadata user-script is before
    //             handleChangeMetadataValue(fieldName, value);
    //             break;
    //         // no default
    //     }
    //
    // }


    const onSelectChange = (event, fieldName) => {

        let selected = event.target.value;
        let relatedTempObj = null;
        let tempFieldNameObj = fieldName + "Obj";

        let tempTab0 = state.tab0;
        let tempTab1 = state.tab1;
        let tempTab2 = state.tab2;
        let tempTab3 = state.tab3;
        let tempTab4 = state.tab4;

        let tempMetadataName = "";
        let relatedMetadataTagsToDelete = [];

        let tempDisplayObj = metadata.getDefaultMetadataObj();
        let tempDisplayField = "dummyDisplay";

        switch(fieldName) {

            case "appHeader":

                tempTab0 = tabLabels.appHeader[selected === "no" ? 0 : 1];
                tempMetadataName = portalShared.CUSTOM_QNR_APP_HEADER_OBJ;
                relatedTempObj = (selected === "no") ? null : portalShared.getCustomQuestionnaireAppHeaderObj();
                if (selected === "no") {
                    relatedMetadataTagsToDelete = [portalShared.HEADER_APPBAR_IMAGE_LEFT, portalShared.HEADER_APPBAR_IMAGE_CENTER, portalShared.HEADER_APPBAR_IMAGE_RIGHT];
                }
                break;

            case "startPage":

                tempMetadataName = portalShared.CUSTOM_QNR_START_PAGE_OBJ;

                if (selected === "no") {
                    tempTab1 = tabLabels.startPage[0];
                    relatedTempObj = null;
                    relatedMetadataTagsToDelete = [];
                    relatedMetadataTagsToDelete.push(portalShared.QNR_START_PAGE_SCRIPT); // Script Text
                    relatedMetadataTagsToDelete.push(portalShared.QNR_START_PAGE_SCRIPT_FORMAT_OBJ); // Script Text Format Object
                    tempDisplayField = "startPageScriptDisplay"; // Reset Portal Display back to default
                } else {
                    tempTab1 = tabLabels.startPage[1];
                    relatedTempObj = portalShared.getCustomQuestionnaireStartPageObj();
                }
                break;

            case "mainPages":

                tempTab2 = tabLabels.mainPages[selected === "default" ? 0 : 1];
                tempMetadataName = portalShared.CUSTOM_QNR_MAIN_PAGES_OBJ;
                relatedTempObj = (selected === "default") ? null : portalShared.getCustomQuestionnaireMainPagesObj();
                break;

            case "closePage":

                tempMetadataName = portalShared.CUSTOM_QNR_CLOSE_PAGE_OBJ;

                if (selected === "no") {
                    tempTab3 = tabLabels.closePage[0];
                    relatedTempObj = null;
                    relatedMetadataTagsToDelete = [];
                    relatedMetadataTagsToDelete.push(portalShared.QNR_CLOSE_PAGE_SCRIPT); // Script Text
                    relatedMetadataTagsToDelete.push(portalShared.QNR_CLOSE_PAGE_SCRIPT_FORMAT_OBJ); // Script Text Format Object
                    tempDisplayField = "closePageScriptDisplay"; // Reset Portal Display back to default
                } else {
                    tempTab3 = tabLabels.closePage[1];
                    relatedTempObj = portalShared.getCustomQuestionnaireClosePageObj();
                }
                break;

            case "system":

                tempTab4 = tabLabels.system[selected === "default" ? 0 : 1];
                tempMetadataName = portalShared.CUSTOM_QNR_SYSTEM_OBJ;
                tempFieldNameObj = "systemObj";
                relatedTempObj = (selected === "default") ? null : portalShared.getCustomQuestionnaireSystemObj();
                break;
            // no default
        }

        if (selected === "no" &&
            (fieldName === "startPage" || fieldName === "closePage")) {
            discardPendingPageTextUpdate(tempMetadataName);
        }

        setState({
            tab0: tempTab0,
            tab1: tempTab1,
            tab2: tempTab2,
            tab3: tempTab3,
            tab4: tempTab4,
            [fieldName]: selected,
            [tempDisplayField]: tempDisplayObj,
            [tempFieldNameObj]: relatedTempObj
        }, () => setQuestionnaireCustomMetadata(tempFieldNameObj, fieldName, selected, tempMetadataName, relatedTempObj, relatedMetadataTagsToDelete));

    };

    // #################################################################################################################
    // -----------------------------------------------------------------------------------------------------------------
    // APP TITLE BAR - LEFT, CENTER & RIGHT - HEADER FUNCTIONS
    // -----------------------------------------------------------------------------------------------------------------
    // #################################################################################################################

    const onSelectChangeTitle = (event, fieldName) => {

        let selected = event.target.value;

        //let adjustedFieldName = fieldName.includes("Type") ? fieldName.replace("Type","") : fieldName;

        let tempMetadataObj = null;
        let stateObjName = titleType[fieldName][TITLE_TYPE_STATE_OBJ];
        let textEntryType = titleType[fieldName][TITLE_TYPE_TEXT_ENTRY_TYPE];
        let metadataObjTag = titleType[fieldName][TITLE_TYPE_METADATA_OBJ_TAG];
        let metadataImageTag = titleType[fieldName][TITLE_TYPE_METADATA_IMAGE_TAG];
        let textObjName = fieldName + "TextObj";
        let removeMetadataTag = null;

        if (selected !== "image") {
            removeMetadataTag = metadataImageTag;
        }

        tempMetadataObj = portalShared.getMetadataObj(template.metadata, metadataObjTag);
        tempMetadataObj[fieldName + "Type"] = selected;
        if (selected === "text" || selected.includes("pageNum") || selected.includes("pageofNum")) {
            tempMetadataObj[textObjName] = portalShared.getCustomTextObj(TEMPLATE.TYPE.QUESTIONNAIRE, textEntryType, selected);
        } else {
            tempMetadataObj[textObjName] = null; // none selected so remove custom text object
        }

        setState({[stateObjName]: tempMetadataObj}, () => handleChangeMetadataValue(metadataObjTag, JSON.stringify(tempMetadataObj), removeMetadataTag));
    }

    const onTextChangeAppHeaderTitle = (event, fieldName) => {

        let tempMetadataObj = null;
        let stateObjName = titleType[fieldName][TITLE_TYPE_STATE_OBJ];
        let metadataObjTag = titleType[fieldName][TITLE_TYPE_METADATA_OBJ_TAG];

        tempMetadataObj = portalShared.getMetadataObj(template.metadata, metadataObjTag);
        tempMetadataObj[fieldName + "TextObj"].title = event.target.value;

        setState({[stateObjName]: tempMetadataObj});
        handleChangeMetadataValueDebounced(metadataObjTag, JSON.stringify(tempMetadataObj));
    };

    const onSelectChangeTitleTextFormat = (event, fieldName) => {
        let tempMetadataObj = null;
        let stateObjName = titleType[fieldName][TITLE_TYPE_STATE_OBJ];
        let metadataObjTag = titleType[fieldName][TITLE_TYPE_METADATA_OBJ_TAG];

        tempMetadataObj = portalShared.getMetadataObj(template.metadata, metadataObjTag);
        tempMetadataObj[fieldName + "TextObj"].format = event.target.value;

        setState({[stateObjName]: tempMetadataObj}, () => handleChangeMetadataValue(metadataObjTag, JSON.stringify(tempMetadataObj)));
    }

    const updateCustomTitleObject = (fieldNameObj, fieldName, selected, metadataTag, metadataValueObj) => {

        let updatedTemplate = {...template};
        let tempMetadata = {...updatedTemplate.metadata};

        let tempMetadataObj = JSON.parse(tempMetadata[metadataTag]);

        tempMetadataObj[fieldName] = metadataValueObj;

        setState({[fieldNameObj]: tempMetadataObj});

        handleChangeMetadataValue(metadataTag, JSON.stringify(tempMetadataObj));
    };

    // -----------------------------------------------------------------------------------------------------------------
    // SCRIPT FUNCTIONS
    // -----------------------------------------------------------------------------------------------------------------

    const onChangeMainPagesObj = (event, fieldName) => {

        let tempStateObj = portalShared.cloneObj(state.mainPagesObj);

        tempStateObj[fieldName] = event.target.value;

        if (fieldName === "questionNumbering" && event.target.value === "none") {
            tempStateObj["questionNums"] = "none"; // AKA Question Format
        }

        setState({mainPagesObj: tempStateObj});

        let updatedTemplate = portalShared.cloneObj(template); // Prevent mutation errors

        updatedTemplate.metadata[portalShared.CUSTOM_QNR_MAIN_PAGES_OBJ] = JSON.stringify(tempStateObj);

        dispatch(updateQuestionnaireTemplate(updatedTemplate));
    };

    // -----------------------------------------------------------------------------------------------------------------
    // IMAGE UPLOAD FROM ArgoMetadataEditor  ---------------------------------------------------------------------------
    // -----------------------------------------------------------------------------------------------------------------

    const handleLogoUploadOnChange = (e, metadataTag) => {
        let file = e.target.files[0];
        if (file) {
            compressImage(file).then((dataUri) => {
                setImageMetadata(metadataTag, dataUri);
            }).catch((err) => {
                dispatch(notificationOpen(err.message));
                e.target.value = "";
            });
        }
    };

    const onChangeIsThisAnExistingCategory = (event) => {

        let newState = {
            isThisAnExistingCategory: "1",
            categoryTemplateSearch: state.categoryTemplateSearch
        };

        setState(newState);
    }

    const handleTabChange = (event, selectedTabNumber) => {
        setState({ selectedTabNumber: selectedTabNumber });
    };


    const setColorForPortalHeaderBar = (colorObj) => {

        let appHeaderObj = portalShared.cloneObj(state.appHeaderObj);

        if (appHeaderObj === null) {
            appHeaderObj = portalShared.getCustomQuestionnaireAppHeaderObj();
        }

        if (colorObj["colorObj"].colorType === "default") {
            appHeaderObj.colorObj = {...portalShared.customColorAppHeaderObject}
        } else  {
            appHeaderObj.colorObj = colorObj["colorObj"];
        }

        setState({ appHeaderObj: appHeaderObj });


        let updatedTemplate = portalShared.cloneObj(template); // Prevent mutation errors


        if (!updatedTemplate.hasOwnProperty("metadata")) {
            updatedTemplate.metadata = {};
            updatedTemplate.metadata[portalShared.CUSTOM_QNR_APP_HEADER_OBJ] = {...appHeaderObj};
        }

        let parsedUpdatedTemplate = JSON.parse(updatedTemplate.metadata[portalShared.CUSTOM_QNR_APP_HEADER_OBJ]);

        parsedUpdatedTemplate.colorObj = {...appHeaderObj.colorObj};

        updatedTemplate.metadata[portalShared.CUSTOM_QNR_APP_HEADER_OBJ] = JSON.stringify(parsedUpdatedTemplate);

        dispatch(updateQuestionnaireTemplate(updatedTemplate));
    };

    const getTab0_Header = (propsObj) => {

        return (
            <div id="QuestionnaireEditor-HeaderTab-container-div" style={{paddingLeft: "20px", paddingBottom: "20px"}}>

                <div>&nbsp;</div>

                <ArgoTextFieldSelectHook
                    fileName="QuestionnaireEditor"
                    fieldName="appHeader"
                    label="Application Header"
                    value={state.appHeader}
                    onChange={onSelectChange}
                    width="170px"
                    menuItems={arrayNoOrYes}
                />

                <div style={{width: "20px"}}></div>

                {state.appHeader === "yes" ?
                    <React.Fragment>
                        <div style={{display: "flex", alignItems: "left", width: "100%"}}>
                            <ArgoColorInputHook mode="title"
                                                colorLabel={"Header Bar Color"}
                                                propsFieldName={"appHeaderObj"}
                                                initToObj={state.appHeaderObj.colorObj}
                                                opacityFlag={true}
                                                largeHexEntry={false}
                                                setColor={setColorForPortalHeaderBar}
                                                height={'17px'}
                            />
                        </div>

                        <div>Header Bar Contents:</div>
                        <div>&nbsp;</div>

                        <div>
                            {getTitleDiv("Left", "appHeaderLeft", propsObj)}
                        </div>
                        <div>
                            {getTitleDiv("Center", "appHeaderCenter", propsObj)}
                        </div>
                        <div>
                            {getTitleDiv("Right", "appHeaderRight", propsObj)}
                        </div>
                    </React.Fragment> : ""
                }

            </div>
        );
    };

    const getTitleDiv = (titleLabel, titlePrefix, propsObj) => {

        let tempTitleType = titlePrefix + "Type";
        let tempTitleTextObj = titlePrefix + "TextObj";

        let stateObjName = titleType[titlePrefix][TITLE_TYPE_STATE_OBJ];
        let metadataObjTag = titleType[titlePrefix][TITLE_TYPE_METADATA_OBJ_TAG];
        let metadataImageTag = titleType[titlePrefix][TITLE_TYPE_METADATA_IMAGE_TAG];

        let image = null;

        let metadata = {...template.metadata};
        if (metadata.hasOwnProperty(metadataImageTag)) {
            image = metadata[metadataImageTag];
        }

        return (
            <div id={"Qnr-Title-" + titlePrefix} style={{display: "flex", alignItems: "left", width: '99%', backgroundColor: 'white'}}>
                <div style={{width: "20px"}}></div>

                <div style={{padding: '10px 10px 10px 0'}}>
                    <ArgoTextFieldSelectHook
                        fileName="QuestionnaireEditor"
                        fieldName={titlePrefix}
                        label={titleLabel}
                        value={state[stateObjName][tempTitleType]}
                        onChange={onSelectChangeTitle}
                        width="80px"
                        menuItems={arrayNoneOrTextOrImageOrPageNum}
                    /></div>

                <div style={{width: "20px"}}></div>

                {state[stateObjName][tempTitleType] === "none" ?
                    <div style={{
                        paddingTop: "20px",
                        display: "flex",
                        alignItems: "left",
                        width: '99%',
                        backgroundColor: 'white'
                    }}>
                    </div> : ""
                }

                {state[stateObjName][tempTitleType] === "image" ?
                    <div id={metadataImageTag + "-div"} style={{
                        paddingTop: "30px",
                        display: "flex",
                        alignItems: "left",
                        width: '99%',
                        backgroundColor: 'white'
                    }}>

                        <form id={metadataImageTag + "-form"}onChange={(e) => handleLogoUploadOnChange(e, metadataImageTag)}>
                            <input
                                type="file"
                                name="image"
                                id={metadataImageTag +`file${titleLabel}`}
                                accept=".jpeg, .png, .jpg, .webp, .svg"
                                style={{display: "none"}}
                            />
                            <label
                                htmlFor={metadataImageTag +`file${titleLabel}`}
                                style={{
                                    display: "inline-block",
                                    backgroundColor: "#2497F3",
                                    color: "white",
                                    padding: "5px 10px",
                                    borderRadius: "5px",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                    marginRight: "40px"
                                }}
                            >
                                {"Choose Image"}
                            </label>
                        </form>
                        {image === null ? "" :
                            <img src={imageDataUri(image)}
                                 alt=""
                                 style={{
                                     verticalAlign: 'middle',
                                     height: '60px'}}
                            />
                        }

                    </div> : ""
                }

                {(state[stateObjName][tempTitleType] === "text" || state[stateObjName][tempTitleType].includes("pageNum")  || state[stateObjName][tempTitleType].includes("pageofNum")) ?
                    <div style={{
                        display: "flex",
                        alignItems: "left",
                        width: '99%',
                        backgroundColor: 'white',
                        paddingTop: '10px'
                    }}>

                        {(state[stateObjName][tempTitleType] === "text" && !state[stateObjName][tempTitleTextObj].title.includes("pageNum") && !state[stateObjName][tempTitleTextObj].title.includes("pageofNum")) ?
                            <ArgoTextFieldHook
                                fileName="QuestionnaireEditor"
                                fieldName={titlePrefix}
                                label={titleLabel + " Text"}
                                value={state[stateObjName][tempTitleTextObj].title}
                                onChange={onTextChangeAppHeaderTitle}
                                width="200px"
                                maxLength={40}
                            /> : <div style={{color: "#01579B", width: "250px", paddingTop: "20px"}}>{state[stateObjName][tempTitleTextObj].title.includes("pageNum") ? "Page X" : "Page X of Y"}</div>
                        }

                        <div style={{width: "15px"}}></div>

                        <ArgoTextFieldSelectHook
                            fileName="QuestionnaireEditor"
                            fieldName={titlePrefix}
                            label="Format"
                            value={state[stateObjName][tempTitleTextObj].format}
                            onChange={onSelectChangeTitleTextFormat}
                            width="90px"
                            menuItems={arrayDefaultOrCustom}
                        />

                        <div style={{width: '99%'}}>
                            {(state[stateObjName][tempTitleTextObj].format === 'custom') ?
                                <ArgoTextDisplayCustomHook
                                    fieldName={tempTitleTextObj}
                                    fieldTextObjName={stateObjName}
                                    initToObj={state[stateObjName][tempTitleTextObj]}
                                    metadataTag={metadataObjTag}
                                    showDisplay={false}
                                    backgroundColorOverride={"white"}
                                    metadataPropsObj={Object.assign({metadataTag: metadataObjTag}, propsObj)}
                                    useMetadataPropsObjAltFunction={true}
                                    largeHexEntry={false}
                                    paddingTop={'0'}
                                    setCustomMetadata={updateCustomTitleObject}
                                />
                                : ""
                            }
                        </div>
                    </div> : ""
                }
            </div>
        );
    }

    // -----------------------------------------------------------------------------------------------------------------
    // START PAGE SECTION & CLOSE PAGE SECTION
    // -----------------------------------------------------------------------------------------------------------------

    const onCheckboxChangeStartPage = (event) => {

        let updatedTemplate = {...template};  // Copy template
        let metadata = {...updatedTemplate.metadata};  // Copy metadata from template

        let metadataObj = JSON.parse(metadata[portalShared.CUSTOM_QNR_START_PAGE_OBJ]);

        metadataObj.hideName = event.target.checked;

        metadata[portalShared.CUSTOM_QNR_START_PAGE_OBJ] = JSON.stringify(metadataObj);

        updatedTemplate.metadata = metadata; // Update template with new metadata
        dispatch(updateQuestionnaireTemplate(updatedTemplate)); // Update questionnaire with new metadata

        setState({hideNameOnStartPage: event.target.checked});
    };

    const onCheckboxChangeClosePage = (event) => {

        let updatedTemplate = {...template};  // Copy template
        let metadata = {...updatedTemplate.metadata};  // Copy metadata from template

        let metadataObj = JSON.parse(metadata[portalShared.CUSTOM_QNR_CLOSE_PAGE_OBJ]);

        metadataObj.noGreenCheck = event.target.checked;

        metadata[portalShared.CUSTOM_QNR_CLOSE_PAGE_OBJ] = JSON.stringify(metadataObj);

        updatedTemplate.metadata = metadata; // Update template with new metadata
        dispatch(updateQuestionnaireTemplate(updatedTemplate)); // Update questionnaire with new metadata

        setState({noGreenCheck: event.target.checked});
    };

    const onStartOrClosePageScriptChange = (event, fieldName) => {

        let value = event.target.value;
        handleChangeMetadataValueDebounced(fieldName, value, value === "" ? fieldName : null);
    }

    const setStateStartOrClosePage = (stateObj) => {

        let prefix = Object.keys(stateObj)[0].substring(0,9); // Will get "startPage or closePage

        let metadataTag = (prefix === "startPage") ? portalShared.CUSTOM_QNR_START_PAGE_OBJ : portalShared.CUSTOM_QNR_CLOSE_PAGE_OBJ;

        if (stateObj.hasOwnProperty(prefix + "ScriptDisplay")) {
            setState({[prefix + "ScriptDisplay"]: stateObj[prefix + "ScriptDisplay"]});
        } else if (stateObj.hasOwnProperty(prefix + "TextDisplay")) {

            let updatedTemplate = {...template};  // Copy template
            let metadata = {...updatedTemplate.metadata};  // Copy metadata from template

            let metadataObj = JSON.parse(metadata[metadataTag]);

            if (stateObj[prefix + "TextDisplay"]?.selected === "custom") {
                metadataObj.defaultTextFormat = "custom";
                metadataObj.defaultTextCustomObj = stateObj[prefix + "TextDisplay"].metadataValueObj;
                metadataObj.defaultTextCustomObj.format = "custom";
            } else {
                metadataObj.defaultTextFormat = "default";
                metadataObj.defaultTextCustomObj = null;
            }

            metadata[metadataTag] = JSON.stringify(metadataObj);

            setState({[prefix + "TextDisplay"]: stateObj[prefix + "TextDisplay"]});

            updatedTemplate.metadata = metadata; // Update template with new metadata
            dispatch(updateQuestionnaireTemplate(updatedTemplate)); // Update questionnaire with new metadata
        }
    }

    const onTextChangeStartOrClosePageDefaultText = (event, fieldName) => {

        let metadataTag = (fieldName === "startPageText") ? portalShared.CUSTOM_QNR_START_PAGE_OBJ : portalShared.CUSTOM_QNR_CLOSE_PAGE_OBJ;

        let value = event.target.value;
        setState({[fieldName]: value});

        clearTimeout(pageTextDebounceRef.current);
        pendingPageTextUpdateRef.current = queueQuestionnairePageTextUpdate(
            pendingPageTextUpdateRef.current,
            metadataTag,
            value
        );
        pageTextDebounceRef.current = setTimeout(applyPendingPageTextUpdate, 300);
    };

    const getTab_StartOrClosePage = (tabStr, pageType, propsObj) => {

        let label = "Start"
        let pageMetadataTag = portalShared.CUSTOM_QNR_START_PAGE_OBJ;
        let scriptType = TEMPLATE.TEXT_TYPE.QUESTIONNAIRE.START_PAGE_SCRIPT
        let scriptMetadataTag = portalShared.QNR_START_PAGE_SCRIPT;
        let scriptMetadataFormatTag = portalShared.QNR_START_PAGE_SCRIPT_FORMAT_OBJ;
        let textType = TEMPLATE.TEXT_TYPE.QUESTIONNAIRE.START_PAGE_TEXT;

        let tempDefaultCustomObj = state[pageType + "TextDisplay"]?.metadataValueObj;

        // It seems this property is not being used correctly all the time
        if (tempDefaultCustomObj && (typeof tempDefaultCustomObj === "object") && tempDefaultCustomObj.hasOwnProperty("defaultTextCustomObj")) {
            tempDefaultCustomObj = tempDefaultCustomObj.defaultTextCustomObj;
        }

        if (pageType === "closePage") {
            label = "Close"
            pageMetadataTag = portalShared.CUSTOM_QNR_CLOSE_PAGE_OBJ;
            scriptType = TEMPLATE.TEXT_TYPE.QUESTIONNAIRE.CLOSE_PAGE_SCRIPT
            scriptMetadataTag = portalShared.QNR_CLOSE_PAGE_SCRIPT;
            scriptMetadataFormatTag = portalShared.QNR_CLOSE_PAGE_SCRIPT_FORMAT_OBJ;
            textType = TEMPLATE.TEXT_TYPE.QUESTIONNAIRE.CLOSE_PAGE_TEXT;
        }

        propsObj.onTextChange = onStartOrClosePageScriptChange;
        propsObj.setStateCallbackFunction = setStateStartOrClosePage;

        let metadataLoaded = template.hasOwnProperty("metadata");

        return (
            <div id={"QuestionnaireEditor-" + pageType + "Tab-container-div"}
                 style={{paddingLeft: "20px", paddingBottom: "20px"}}>

                <div>&nbsp;</div>

                <div style={{display: "flex", alignItems: "left", width: '99%'}}>

                    <ArgoTextFieldSelectHook
                        fileName="QuestionnaireEditor"
                        fieldName={pageType}
                        label={label + " Page"}
                        value={state[pageType]}
                        onChange={onSelectChange}
                        width="170px"
                        menuItems={arrayNoOrYes}
                    />

                </div>

                {(state[pageType] === "yes") && metadataLoaded ?
                    <React.Fragment>
                        <div style={{width: '95%', padding: "20px"}}>

                            {pageType === "startPage" ?
                                <div style={{
                                    display: "flex",
                                    alignItems: "left",
                                    backgroundColor: 'white',
                                    paddingLeft: "20px"
                                }}>
                                    <ArgoCheckboxHook
                                        fileName="QuestionnaireEditor"
                                        fieldName="hideNameOnStartPage"
                                        label="Do not display questionnaire name"
                                        onChange={onCheckboxChangeStartPage}
                                        checked={state.hideNameOnStartPage}
                                        width="300px"
                                    />
                                </div>
                                :
                                <div style={{
                                    display: "flex",
                                    alignItems: "left",
                                    backgroundColor: 'white',
                                    paddingLeft: "20px"
                                }}>
                                    <ArgoCheckboxHook
                                        fileName="QuestionnaireEditor"
                                        fieldName="noGreenCheck"
                                        label="No green check mark"
                                        onChange={onCheckboxChangeClosePage}
                                        checked={state.noGreenCheck}
                                        width="300px"
                                    />
                                </div>
                            }

                            {pageType === "startPage" ? <div style={{height: "20px"}}>&nbsp;</div> : ""}

                            <div style={{display: "flex", alignItems: "left", backgroundColor: 'white'}}>

                                <div style={{width: "20px"}}></div>

                                <div style={{display: "flex", alignItems: "left", width: '99%', padding: '10px 0'}}>

                                    <ArgoTextEntryWithCustomOverrideHook
                                        templateType={TEMPLATE.TYPE.QUESTIONNAIRE}
                                        textEntryType={scriptType}
                                        fieldName={scriptMetadataTag}
                                        fieldTextObjName={pageType + "ScriptDisplay"}
                                        label={label + " Page Script"}
                                        labelCustom={"Display (" + label + " Script)"}
                                        value={template.metadata[scriptMetadataTag]}
                                        maxlength={500}
                                        hideOption={false}
                                        initTo={state[pageType + "ScriptDisplay"]?.selected ?? "default"}
                                        initToObj={state[pageType + "ScriptDisplay"]?.metadataValueObj}
                                        propsObj={Object.assign({metadataTag: scriptMetadataFormatTag}, propsObj)}
                                    />

                                </div>
                            </div>

                        </div>

                        <div style={{width: '95%', padding: "20px"}}>

                            <div style={{display: "flex", alignItems: "left", backgroundColor: 'white'}}>

                                <div style={{width: "20px"}}></div>

                                <div style={{display: "flex", alignItems: "left", width: "99%", padding: "10px 0"}}>
                                    <ArgoTextFieldHook
                                        fileName={'QuestionnaireEditor'}
                                        fieldName={pageType + "Text"}
                                        label="Default Text"
                                        variant={"standard"}
                                        maxLength={"500"}
                                        multiline={true}
                                        onChange={onTextChangeStartOrClosePageDefaultText}
                                        value={state[pageType + "Text"]}
                                        width="100%"
                                    />

                                    <ArgoSelectPortalTextDisplayTypeHook
                                        templateType={TEMPLATE.TYPE.QUESTIONNAIRE}
                                        textEntryType={textType}
                                        fieldName={pageType + "TextDisplay.selected"}
                                        fieldTextObjName={pageType + "TextDisplay.selected"}
                                        hideOption={false}
                                        initTo={state[pageType + "TextDisplay"]?.selected ?? "default"}
                                        metadataPropsObj={Object.assign({metadataTag: pageMetadataTag}, propsObj)}
                                    />
                                </div>

                            </div>

                            {(state[pageType + "TextDisplay"]?.selected === "custom")  ?
                                <div style={{paddingLeft: "18px"}}>
                                    <ArgoTextDisplayCustomHook
                                        fieldName={pageType + "TextDisplay"}
                                        fieldTextObjName={pageType + "TextDisplay"}
                                        initToObj={tempDefaultCustomObj}
                                        metadataTag={pageMetadataTag}
                                        showDisplay={true}
                                        displayLabel={"Display (" + label + " Page Text)"}
                                        metadataPropsObj={Object.assign({metadataTag: pageMetadataTag}, propsObj)}
                                    />
                                </div>
                                :
                                ""
                            }

                        </div>
                    </React.Fragment> : ""
                }

            </div>
        );

    };

    // -----------------------------------------------------------------------------------------------------------------
    // MAIN PAGES SECTION
    // -----------------------------------------------------------------------------------------------------------------

    const getTab2_MainPages = (propsObj) => {

        return (
            <div id="QuestionnaireEditor-MainPagesTab-container-div"
                 style={{paddingLeft: "20px", paddingBottom: "20px"}}>

                <div>&nbsp;</div>

                <ArgoTextFieldSelectHook
                    fileName="QuestionnaireEditor"
                    fieldName="mainPages"
                    label="Main Pages Format"
                    value={state.mainPages}
                    onChange={onSelectChange}
                    width="170px"
                    menuItems={arrayDefaultOrCustom}
                />

                <div style={{width: "20px"}}></div>

                {state.mainPages === "custom" ?
                    <React.Fragment>
                        <div style={{width: '95%', padding: "20px 20px 0 20px"}}>
                            <div style={{display: "flex", alignItems: "left", width: '99%', backgroundColor: 'white'}}>
                                <div style={{width: "20px"}}></div>

                                <div style={{
                                    display: "flex",
                                    alignItems: "left",
                                    width: '99%',
                                    backgroundColor: 'white'
                                }}>
                                    <div style={{padding: '10px 10px 10px 0'}}>
                                        <ArgoTextFieldSelectHook
                                            fileName="QuestionnaireEditor"
                                            fieldName="pagingMode"
                                            label="Paging"
                                            value={state.mainPagesObj.pagingMode}
                                            onChange={onChangeMainPagesObj}
                                            width="220px"
                                            menuItems={[
                                                {value: "full", text: "Full Page"},
                                                {value: "one", text: "One Question Per Page"}
                                            ]}/>
                                    </div>

                                    <div style={{width: '20px'}}></div>

                                    <div style={{paddingTop: '10px'}}>
                                        <ArgoTextFieldSelectHook
                                            fileName="QuestionnaireEditor"
                                            fieldName="answeredFirstQuestion"
                                            label="Answered First Question"
                                            value={state.mainPagesObj.answeredFirstQuestion}
                                            onChange={onChangeMainPagesObj}
                                            width="220px"
                                            menuItems={[
                                                {value: "show", text: "Show"},
                                                {value: "hide", text: "Hide"}
                                            ]}/>
                                    </div>

                                    <div style={{width: '20px'}}></div>

                                    <div style={{paddingTop: '10px'}}>
                                        <ArgoTextFieldSelectHook
                                            fileName="QuestionnaireEditor"
                                            fieldName="questionNumbering"
                                            label="Question Numbering"
                                            value={state.mainPagesObj.questionNumbering}
                                            onChange={onChangeMainPagesObj}
                                            width="200px"
                                            menuItems={[
                                                {value: "none", text: "None"},
                                                {value: "all", text: "All Pages"},
                                                {value: "page", text: "Reset Each Page"}
                                            ]}/>
                                    </div>

                                    <div style={{width: '20px'}}></div>

                                    {state.mainPagesObj.questionNumbering !== "none" ?
                                        <React.Fragment>

                                            <div style={{paddingTop: '10px'}}>
                                                <ArgoTextFieldSelectHook
                                                    fileName="QuestionnaireEditor"
                                                    fieldName="questionNums"
                                                    label="Number Format"
                                                    value={state.mainPagesObj.questionNums}
                                                    onChange={onChangeMainPagesObj}
                                                    width="200px"
                                                    menuItems={[
                                                        {value: "none", text: "None"},
                                                        {value: "#", text: "#"},
                                                        {value: "#.", text: "#."},
                                                        {value: "#)", text: "#)"}
                                                    ]}/>
                                            </div>

                                            {useQuestionSubNumbers ?
                                                <div>
                                                    <div style={{width: '15px'}}></div>

                                                    <div style={{paddingTop: '10px'}}>
                                                        <ArgoTextFieldSelectHook
                                                            fileName="QuestionnaireEditor"
                                                            fieldName="questionNumsLikert"
                                                            label="Likert Sub-Numbers"
                                                            value={state.mainPagesObj.questionNumsLikert}
                                                            onChange={onChangeMainPagesObj}
                                                            width="200px"
                                                            menuItems={arrayQuestionSubNumberFormats}/>
                                                    </div>

                                                    <div style={{width: '15px'}}></div>

                                                    <div style={{paddingTop: '10px'}}>
                                                        <ArgoTextFieldSelectHook
                                                            fileName="QuestionnaireEditor"
                                                            fieldName="questionNumsShowGroup"
                                                            label="Condition Sub-Numbers"
                                                            value={state.mainPagesObj.questionNumsShowGroup}
                                                            onChange={onChangeMainPagesObj}
                                                            width="200px"
                                                            menuItems={arrayQuestionSubNumberFormats}/>
                                                    </div>
                                                </div> : ""
                                            }
                                        </React.Fragment> : ""
                                    }
                                </div>
                            </div>

                            {getMainPagesTitles("Header", "pageHeader", propsObj)}

                            {getMainPagesTitles("Footer 1", "pageFooter1", propsObj)}

                            {getMainPagesTitles("Footer 2", "pageFooter2", propsObj)}

                        </div>
                    </React.Fragment> : ""
                }

            </div>
        );
    }

    const getMainPagesTitles = (label, titlePrefix, propsObj) => {

        return (
            <div style={{display: "flex", alignItems: "left", width: '99%', backgroundColor: 'white'}}>
                <div style={{width: "20px"}}></div>

                <div style={{padding: '10px 10px 10px 0'}}>
                    <ArgoTextFieldSelectHook
                        fileName="QuestionnaireEditor"
                        fieldName={titlePrefix}
                        label={label}
                        value={state.mainPagesObj[titlePrefix]}
                        onChange={onChangeMainPagesObj}
                        width="120px"
                        menuItems={arrayNoOrYes}/>
                </div>

                <div style={{width: "20px"}}></div>

                <div style={{width: '99%'}}>

                    {state.mainPagesObj[titlePrefix] === "yes" ?
                        <React.Fragment>
                            <div>
                                {getTitleDiv("Left", titlePrefix + "Left", propsObj)}
                            </div>
                            <div>
                                {getTitleDiv("Center", titlePrefix + "Center", propsObj)}
                            </div>
                            <div>
                                {getTitleDiv("Right", titlePrefix + "Right", propsObj)}
                            </div>
                        </React.Fragment> : ""
                    }
                </div>

            </div>);
    };

    // -----------------------------------------------------------------------------------------------------------------
    // SYSTEM SECTION
    // -----------------------------------------------------------------------------------------------------------------

    const onCheckboxChangeObj = (event, fieldName, stateObjName, metadataTag) => {
        setMetadataObject(event.target.checked, fieldName, stateObjName, metadataTag)
    };

    const onSelectChangeObj = (event, fieldName, menuItemText, index, stateObjName, metadataTag) => {
        setMetadataObject(event.target.value, fieldName, stateObjName, metadataTag)
    };

    const setMetadataObject = (value, fieldName, stateObjName, metadataTag) => {
        let updatedTemplate = {...template};  // Copy template
        let metadata = {...updatedTemplate.metadata};  // Copy metadata from template

        let metadataObj = JSON.parse(metadata[metadataTag]);

        let stateObj = portalShared.cloneObj(state[stateObjName]);

        let fieldNameArray = fieldName.split(".");

        switch(fieldNameArray.length) {
            case 1:
                metadataObj[fieldNameArray[0]] = value;
                stateObj[fieldNameArray[0]] = value;
                break;
            case 2:
                metadataObj[fieldNameArray[0]][fieldNameArray[1]] = value;
                stateObj[fieldNameArray[0]][fieldNameArray[1]] = value;
                break;
            case 3:
                metadataObj[fieldNameArray[0]][fieldNameArray[1]][fieldNameArray[2]] = value;
                stateObj[fieldNameArray[0]][fieldNameArray[1]][fieldNameArray[2]] = value;
                break;
            // no default
        }

        if (fieldNameArray[0] === "systemButtons" && value === "default") {
            metadataObj[fieldNameArray[0]] = portalShared.getCustomQuestionnaireSystemObjSystemButtonsDefault(); // Reset to default button settings
        }

        metadata[metadataTag] = JSON.stringify(metadataObj);

        updatedTemplate.metadata = metadata; // Update template with new metadata
        dispatch(updateQuestionnaireTemplate(updatedTemplate)); // Update questionnaire with new metadata

        setState({[stateObjName]: stateObj});
    }

    const changeSystemButtonsMetadata = (metadataTag, propName, value) => {
        let updatedTemplate = portalShared.cloneObj(template);  // Clone template

        let metadata = {...updatedTemplate.metadata}; // Copy metadata from template

        let metadataObj = JSON.parse(metadata[metadataTag]);

        metadataObj.systemButtons[propName] = value;
        metadata[metadataTag] = JSON.stringify(metadataObj);
        updatedTemplate.metadata = metadata; // Update template with new metadata

        dispatch(updateQuestionnaireTemplate(updatedTemplate)); // Update questionnaire with new metadata
    }

    const setTextSystemButtonsMetadata = (fieldTextObjName, fieldName, selected, metadataTag, textObj) => {

        let systemObj = portalShared.cloneObj(state.systemObj);
        systemObj.systemButtons[fieldName] = textObj;

        setState({systemObj: systemObj}, () => changeSystemButtonsMetadata(portalShared.CUSTOM_QNR_SYSTEM_OBJ, fieldName, textObj));
    }

    const setColorSystemButtons = (fieldName, colorObj) => {

        let systemObj = portalShared.cloneObj(state.systemObj);
        systemObj.systemButtons[fieldName] = colorObj;

        setState({systemObj: systemObj}, () => changeSystemButtonsMetadata(portalShared.CUSTOM_QNR_SYSTEM_OBJ, fieldName, colorObj));
    };

    const getTab4_System = (propsObj) => {

        // let customColorsArray = [
        //     "Background",
        //     "Hover Text",
        //     "Hover Background"
        // ];
        //
        // let customColorPropsArray = [
        //     "unselectedBackground",
        //     "hoverText",
        //     "hoverBackground"
        // ];

        return (
            <div id="QuestionnaireEditor-SystemTab-container-div" style={{paddingLeft: "20px", paddingBottom: "20px"}}>

                <div>&nbsp;</div>

                <ArgoTextFieldSelectHook
                    fileName="QuestionnaireEditor"
                    fieldName="system"
                    label="System"
                    value={state.system}
                    onChange={onSelectChange}
                    width="170px"
                    menuItems={arrayDefaultOrCustom}
                />

                <div style={{width: "20px"}}></div>

                {state.system === "custom" ?
                    <React.Fragment>
                        <div style={{
                            alignItems: "left",
                            backgroundColor: 'white',
                            paddingLeft: "20px"
                        }}>
                            <ArgoCheckboxHook
                                fileName="QuestionnaireEditor"
                                fieldName="hideFirstQuestion"
                                stateObjName="systemObj"
                                metadataTag={portalShared.CUSTOM_QNR_SYSTEM_OBJ}
                                label="Hide first question"
                                onChange={onCheckboxChangeObj}
                                checked={state.systemObj.hideFirstQuestion}
                                width="300px"
                            />

                            <div>&nbsp;</div>

                            <ArgoTextFieldSelectHook
                                fileName="QuestionnaireEditor"
                                fieldName="systemButtons.format"
                                stateObjName="systemObj"
                                metadataTag={portalShared.CUSTOM_QNR_SYSTEM_OBJ}
                                label="System Buttons"
                                value={state.systemObj.systemButtons.format}
                                onChange={onSelectChangeObj}
                                width="190px"
                                menuItems={arrayDefaultOrCustom}
                            />
                        </div>

                        {(state.systemObj.systemButtons.format === "custom") ?
                            <div id="QuestionnaireEditor-container-div-system-buttons" style={{backgroundColor: portalShared.COLOR_LIGHT_GRAY_HEX, width: "99%"}}>

                                <div id="QuestionnaireEditor-container-div-system-buttons-text" style={{
                                    width: "100%",
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    justifyContent: "left"
                                }}>
                                    <div style={{width: "20px"}}>&nbsp;</div>

                                    <ArgoTextFieldSelectHook
                                        fileName="QuestionnaireEditor"
                                        fieldName="systemButtons.type"
                                        stateObjName="systemObj"
                                        metadataTag={portalShared.CUSTOM_QNR_SYSTEM_OBJ}
                                        label="Button Type"
                                        value={state.systemObj.systemButtons.type}
                                        onChange={onSelectChangeObj}
                                        width="190px"
                                        menuItems={arrayButtonTypes}
                                    />
                                </div>

                                <div id="QuestionnaireEditor-container-div-system-buttons-text" style={{
                                    width: "100%",
                                    display: "flex",
                                    flexDirection: "row",
                                    justifyContent: "left"
                                }}>

                                    <ArgoTextDisplayCustomHook
                                        templateType={TEMPLATE.TYPE.QUESTIONNAIRE}
                                        textEntryType={TEMPLATE.TEXT_TYPE.QUESTIONNAIRE.PORTAL_SYSTEM_BUTTONS}
                                        fieldName="text"
                                        fieldTextObjName="systemObj"
                                        initToObj={state.systemObj.systemButtons.text}
                                        showDisplay={false}
                                        showSize={false}
                                        showStyle={false}
                                        showOpacity={false}
                                        paddingTop={"0px"}
                                        setCustomMetadata={setTextSystemButtonsMetadata}
                                        metadataPropsObj={Object.assign({metadataTag: portalShared.CUSTOM_QNR_SYSTEM_OBJ}, propsObj)}
                                    />

                                    <div style={{paddingTop: "20px", color: "gray", fontWeight: "bold"}}>On Hover:</div>

                                    <ArgoTextDisplayCustomHook
                                        templateType={TEMPLATE.TYPE.QUESTIONNAIRE}
                                        textEntryType={TEMPLATE.TEXT_TYPE.QUESTIONNAIRE.PORTAL_SYSTEM_BUTTONS}
                                        fieldName="textHover"
                                        fieldTextObjName="systemObj"
                                        initToObj={state.systemObj.systemButtons.textHover}
                                        showDisplay={false}
                                        showFont={false}
                                        showSize={false}
                                        showStyle={false}
                                        showOpacity={false}
                                        paddingTop={"0px"}
                                        setCustomMetadata={setTextSystemButtonsMetadata}
                                        metadataPropsObj={Object.assign({metadataTag: portalShared.CUSTOM_QNR_SYSTEM_OBJ}, propsObj)}
                                    />

                                </div>

                                {/*all show background and hover background*/}

                                <div>&nbsp;</div>

                                <div id="QuestionnaireEditor-container-div-system-buttons-text" style={{
                                    width: "100%",
                                    display: "flex",
                                    flexDirection: "row",
                                    justifyContent: "left"
                                }}>

                                    <div style={{width: "20px"}}>&nbsp;</div>

                                    <ArgoColorInputHook mode="questionSingleSelect"
                                                    colorLabel={"Background Color"}
                                                        propsFieldName={"backgroundColor"}
                                                    initToObj={state.systemObj.systemButtons.backgroundColor}
                                                    setColor={setColorSystemButtons}
                                                    opacityFlag={false}
                                    />

                                    <div>&nbsp;</div>

                                    <ArgoColorInputHook mode="questionSingleSelect"
                                                    colorLabel={"Hover Background Color"}
                                                        propsFieldName={"backgroundColorHover"}
                                                    initToObj={state.systemObj.systemButtons.backgroundColorHover}
                                                    setColor={setColorSystemButtons}
                                                    opacityFlag={false}
                                    />

                                </div>

                                {/*outlined show border and hover border*/}

                                <div>&nbsp;</div>

                                {state.systemObj.systemButtons.type === "outlined" ?
                                    <div id="QuestionnaireEditor-container-div-system-buttons-text" style={{
                                        width: "100%",
                                        display: "flex",
                                        flexDirection: "row",
                                        justifyContent: "left"
                                    }}>

                                        <div id="QuestionnaireEditor-container-div-system-buttons-text" style={{
                                            width: "100%",
                                            display: "flex",
                                            flexDirection: "row",
                                            justifyContent: "left"
                                        }}>

                                            <div style={{width: "20px"}}>&nbsp;</div>

                                            <ArgoColorInputHook mode="questionSingleSelect"
                                                            colorLabel={"Border Color"}
                                                                propsFieldName={"borderColor"}
                                                            initToObj={state.systemObj.systemButtons.borderColor}
                                                            setColor={setColorSystemButtons}
                                                            opacityFlag={false}
                                            />

                                            <div>&nbsp;</div>

                                            <ArgoColorInputHook mode="questionSingleSelect"
                                                            colorLabel={"Hover Border Color"}
                                                                propsFieldName={"borderColorHover"}
                                                            initToObj={state.systemObj.systemButtons.borderColorHover}
                                                            setColor={setColorSystemButtons}
                                                            opacityFlag={false}
                                            />

                                        </div>

                                    </div> : ""
                                }

                            </div>
                            : ""
                        }
                    </React.Fragment> : ""
                }

            </div>
        );
    };

    // -----------------------------------------------------------------------------------------------------------------
    //
    // -----------------------------------------------------------------------------------------------------------------

    const addSelectedCategory = (categoryTemplate) => {

        if (!template.categoryKeys.includes(categoryTemplate.templateKey)) {
            setState({categoryTemplateSearch: ""});
            dispatch(addQuestionnaireCategory(categoryTemplate.templateKey));
            dispatch(getCategoryTemplate(categoryTemplate.templateKey));
        }
    };

    const saveNewCategory = (pTemplate) => {
        const origin = createEditorOrigin(
            EDITOR_TYPES.QUESTIONNAIRE,
            store.getState().questionnaireEditor.instanceId
        );
        return dispatch(createCategoryTemplate(pTemplate, true, false, origin)).then(result => {
            if (templateSaveSucceeded(result)) {
                setState({isThisAnExistingCategory: "0", categoryTemplateSearch: ""});
            }
            return result;
        });
    }

    const closeNewCategory = () => {
        setState({isThisAnExistingCategory: "0", categoryTemplateSearch: ""});
    }

    // -----------------------------------------------------------------------------------------------------------------
    // EDITOR FUNCTIONS ------------------------------------------------------------------------------------------------
    // -----------------------------------------------------------------------------------------------------------------

    const categoryEditorSave = (pCategoryTemplate = null) => {
        const templateToSave = pCategoryTemplate ?? state.categoryTemplate;
        const requestOwner = createEditorOrigin(
            EDITOR_TYPES.CATEGORY,
            store.getState().categoryEditor.instanceId
        );
        return dispatch(saveCategoryTemplate(
            templateToSave,
            false,
            false,
            requestOwner
        )).then(result => {
            if (templateSaveSucceeded(result)) {
                setState({openCategoryEditor: false});
            }
            return result;
        });
    };

    const categoryEditorCancel = () => {
        setState({openCategoryEditor: false});
    };

    const groupEditorSave = (pGroupTemplate = null) => {
        const templateToSave = pGroupTemplate ?? state.groupTemplate;
        return dispatch(saveGroupTemplate(templateToSave)).then(result => {
            if (templateSaveSucceeded(result)) {
                setState({openGroupEditor: false, openGroupEditorComplete: false});
            }
            return result;
        });
    };

    const groupEditorCancel = () => {
        setState({openGroupEditor: false, openGroupEditorComplete: false});
    };

    const questionEditorSave = (pQuestionTemplate = null) => {
        const templateToSave = pQuestionTemplate ?? state.questionTemplate;
        return dispatch(saveQuestionTemplate(templateToSave)).then(result => {
            if (templateSaveSucceeded(result)) {
                setState({openQuestionEditor: false});
            }
            return result;
        });
    };

    const questionEditorCancel = () => {
        setState({openQuestionEditor: false});
    };

    // -----------------------------------------------------------------------------------------------------------------
    // RENDER ----------------------------------------------------------------------------------------------------------
    // -----------------------------------------------------------------------------------------------------------------

    if (state.isLoading) {
        // TODO place a loading screen here
        return null;
    }

    const metadataObj = (template && template.metadata) ? template.metadata : {};

    // Overlay local state onto metadata for fields using ArgoTextFieldHook (no self-managed display)
    const metadataWithLocalOverrides = {
        ...metadataObj,
        [portalShared.PORTAL_TAB_TITLE]: state.portalTabTitle
    };

    // Overlay local template fields for ArgoTextFieldHook fields (nameQualifier, expirationDays)
    const templateWithLocalOverrides = {...template, ...localTemplateFields};

    let propsObj = {
        fileName: "QuestionnaireEditor",
        onTextChange: onTextChange,
        templateType: TEMPLATE.TYPE.QUESTIONNAIRE,
        template: templateWithLocalOverrides,
        metadata: metadataWithLocalOverrides,
        templateUpdateFunction: (updatedTemplate) => dispatch(updateQuestionnaireTemplate(updatedTemplate)),
        setStateCallbackFunction: setStateCallback,
        setCustomMetadataFunction: metadataUtils.setCustomMetadata,
        setCustomMetadataFunctionAlt: setCustomMetadataLayered,
        depth: 0
    }

    //const {palette} = context;  // From blueTheme.js

    // -------------------------------------------------------------------------------------------------------------
    // REVIEW SCREENS ----------------------------------------------------------------------------------------------
    // -------------------------------------------------------------------------------------------------------------

    if (state.openCategoryReview) {
        return <ArgoReviewTemplateHook
            templateType={TEMPLATE.TYPE.CATEGORY}
            templateId={state.selectedCategoryInfoId}
            templateKey={state.selectedCategoryInfoKey}
            selectedTemplate={state.selectedCategoryInfoTemplate}
            handleToolbarButtonClickFunction={handleToolbarButtonClick}
            width={props.width}
        />;
    }
    if (state.openGroupReview) {
        return <ArgoReviewTemplateHook
            templateType={TEMPLATE.TYPE.GROUP}
            templateId={state.selectedGroupInfoId}
            templateKey={state.selectedGroupInfoKey}
            selectedTemplate={state.selectedGroupInfoTemplate}
            handleToolbarButtonClickFunction={handleToolbarButtonClick}
            width={props.width}
        />;
    }
    if (state.openQuestionReview) {
        return <ArgoReviewTemplateHook
                templateType={TEMPLATE.TYPE.QUESTION}
                templateId={state.selectedQuestionInfoId}
                templateKey={state.selectedQuestionInfoKey}
                selectedTemplate={state.selectedQuestionInfoTemplate}
                handleToolbarButtonClickFunction={handleToolbarButtonClick}
                width={props.width}
            />;
        }

        // -------------------------------------------------------------------------------------------------------------
        // EDITOR SCREENS ----------------------------------------------------------------------------------------------
        // -------------------------------------------------------------------------------------------------------------

        if (state.openCategoryEditor) {
            return <CategoryEditorHook
                title="Edit Category"
                save={categoryEditorSave}
                cancel={categoryEditorCancel}
                templateKey={state.categoryTemplate.templateKey}
                templateId={state.categoryTemplate.id}
                filter={props.filter}
                templateMode={TEMPLATE.MODE.CURRENT}
                categoryExpanderEdit={true}
                categoryExpanderSetStateCallback={setStateCallback}
                width={props.width}
                scrollTo="QuestionnaireEditor-default-container-div"
            />;
        }

        if (state.openGroupEditor) {

            //if (!state.openGroupEditorComplete) {

            return <GroupEditor
                title="Edit Group"
                save={groupEditorSave}
                cancel={groupEditorCancel}
                templateKey={state.groupTemplate.templateKey}
                templateId={state.groupTemplate.id}
                filter={props.filter}
                templateMode={TEMPLATE.MODE.CURRENT}
                groupExpanderEdit={true}
                groupExpanderSetStateCallback={setStateCallback}
                width={props.width}
                scrollTo="QuestionnaireEditor-default-container-div"
            />;
            //}
        }

        if (state.openQuestionEditor) {

            return <QuestionEditor
                title="Edit Question"
                save={questionEditorSave}
                cancel={questionEditorCancel}
                templateKey={state.questionTemplate.templateKey}
                templateId={state.questionTemplate.id}
                filter={props.filter}
                templateMode={TEMPLATE.MODE.CURRENT}
                questionDepth={QUESTION_DEPTH}
                questionExpanderEdit={true}
                questionExpanderSetStateCallback={setStateCallback}
                width={props.width}
                scrollTo="QuestionnaireEditor-default-container-div"
            />;
        }

        // -------------------------------------------------------------------------------------------------------------
        // -------------------------------------------------------------------------------------------------------------
        // -------------------------------------------------------------------------------------------------------------

        if (state.isThisAnExistingCategory === "1") {
            let tempTemplate = {
                name: state.categoryTemplateSearch
            };

            return <CategoryEditorHook title="Create Category"
                                   createMode={true}
                                   save={saveNewCategory}
                                   cancel={closeNewCategory}
                                   createInitialTemplate={tempTemplate}
                                   width={props.width}
            />;
        }

        // let portalTabTitle = "";
        // if (template?.metadata) {
        //     portalTabTitle = template.metadata.hasOwnProperty(portalShared.PORTAL_TAB_TITLE) ? template.metadata[portalShared.PORTAL_TAB_TITLE] : ""
        // }

        let questionnaireTemplateClone = portalShared.cloneObj(template);

        questionnaireTemplateClone.categoryKeys = categoryKeys; // Pick up category changes for PortalPreview

        return (
            (props.cloneMode) ?
                <div id="QuestionnaireEditor-clone-container-div">
                    <ArgoToolbarHook
                        fileName="QuestionnaireEditor"
                        fieldName="save-or-cancel"
                        toolbarErrorText={state.toolbarErrorText}
                        isValidating={options.isValidating}
                        onClick={handleToolbarButtonClickWithFlush}
                        width={props.width}
                        templateEditor={templateEditor}
                        options={options}
                    />
                    <div id="QuestionnaireEditor-height-div" style={{height: "50px"}}></div>
                    <div style={{justifyContent: "space-between"}}>
                        <div style={{flexGrow: "2", paddingLeft: "32px", paddingRight: "32px", marginTop: "30px"}}>

                            <ArgoTextFieldHook
                                fileName="QuestionnaireEditor"
                                fieldName="name"
                                label="Questionnaire Name"
                                value={templateWithLocalOverrides.name}
                                onChange={onTextChange}
                            />

                            <ArgoTextFieldHook
                                fileName="QuestionnaireEditor"
                                fieldName="nameQualifier"
                                label="Name Qualifier"
                                value={templateWithLocalOverrides.nameQualifier}
                                maxLength={40}
                                onChange={onTextChange}
                                errorText={state.nameQualifierErrorText}
                                autoFocus={true}
                            />

                        </div>
                    </div>

                    {(template.templateKey !== "") ?
                        <div style={{height: "100%"}}>
                            {(categoryKeys) ?
                                <QuestionnaireReviewHook
                                    templateId={template.id}
                                    templateKey={template.templateKey}/>
                                : ""
                            }
                        </div>
                        :
                        ""}
                </div>
                :
                <div id="QuestionnaireEditor-default-container-div" className="layout vertical" style={{height: "100%", width: "100%"}}>
                    <ArgoToolbarHook
                        fileName="QuestionnaireEditor"
                        fieldName="save-or-cancel"
                        toolbarErrorText={state.toolbarErrorText}
                        isValidating={options.isValidating}
                        onClick={handleToolbarButtonClickWithFlush}
                        width={props.width}
                        templateEditor={templateEditor}
                        options={options}
                    />
                    <div id="QuestionnaireEditor-height-div" style={{height: "50px"}}></div>
                    <div style={{flexGrow: "2", paddingLeft: "32px", paddingRight: "32px", marginTop: '30px'}}>

                        <ArgoTextEntryWithCustomOverrideHook
                            templateType={TEMPLATE.TYPE.QUESTIONNAIRE}
                            textEntryType={TEMPLATE.TEXT_TYPE.QUESTIONNAIRE.NAME}
                            fieldName="name"
                            fieldTextObjName="nameDisplay"
                            label="Questionnaire Name"
                            labelCustom="Display (Name)"
                            value={template.name}
                            maxlength={255}
                            errorText={state.nameErrorText}
                            initTo={state.nameDisplay?.selected ?? "default"}
                            initToObj={state.nameDisplay?.metadataValueObj}
                            propsObj={Object.assign({metadataTag: portalShared.CUSTOM_DDO_NAME}, propsObj)}
                            autoFocus={true}
                        />

                        <div id="QuestionnaireEditor-qualifier-container-div"
                             style={{display: "flex", alignItems: "left", width: "100%"}}>
                            <ArgoTextFieldHook
                                fileName="QuestionnaireEditor"
                                fieldName="nameQualifier"
                                label="Name Qualifier"
                                value={templateWithLocalOverrides.nameQualifier}
                                maxlength={255}
                                onChange={onTextChange}
                                width="96%"
                                divContainerHeight="52px"
                            />

                            <div style={{width: "83px"}}>&nbsp;</div>

                        </div>

                        <div>&nbsp;</div>
                        <div>&nbsp;</div>

                        <ArgoTextEntryWithCustomOverrideHook
                            templateType={TEMPLATE.TYPE.QUESTIONNAIRE}
                            textEntryType={TEMPLATE.TEXT_TYPE.QUESTIONNAIRE.DESC}
                            fieldName="description"
                            fieldTextObjName="descDisplay"
                            label="Description/Instructions"
                            labelCustom="Display (Description)"
                            value={template.description}
                            maxlength={255}
                            initTo={state.descDisplay?.selected ?? "default"}
                            initToObj={state.descDisplay?.metadataValueObj}
                            propsObj={Object.assign({metadataTag: portalShared.CUSTOM_DDO_DESC}, propsObj)}
                            showIndent={true}
                        />

                        <div style={{display: "flex", flexDirection: "row"}}>

                            <ArgoTextFieldHook
                                fileName="QuestionnaireEditor"
                                fieldName="portalTabTitle"
                                label="Portal Tab Title"
                                value={state.portalTabTitle}
                                maxlength={40}
                                width="300px"
                                onChange={onTextChangePortalTabTitle}
                            />

                            <div>&nbsp;&nbsp;&nbsp;</div>

                            <ArgoTextFieldHook
                                fileName="QuestionnaireEditor"
                                fieldName="expirationDays"
                                label="Portal Expiration Days"
                                value={templateWithLocalOverrides.expirationDays}
                                type="number"
                                minNum={0}
                                width="140px"
                                onChange={onTextChange}
                            />

                            {/*<div>&nbsp;&nbsp;&nbsp;</div>*/}

                            {/*// = Changing message issues see JIRA-1739 server does not return anything by error codes*/}
                            {/*// = So having a message embedded in the questionnaire would never get sent to Portal for display*/}

                            {/*<ArgoTextFieldHook*/}
                            {/*    fileName="QuestionnaireEditor"*/}
                            {/*    fieldName="expirationDescription"*/}
                            {/*    label="Portal Expiration Message"*/}
                            {/*    value={template.expirationDescription}*/}
                            {/*    maxlength={255}*/}
                            {/*    width="500px"*/}
                            {/*    onChange={onTextChange}*/}
                            {/*/>*/}


                        </div>

                        {/*// ===========================================================================================================*/}
                        {/*// Questionnaire Classification */}
                        {/*// ===========================================================================================================*/}

                        <ArgoTextFieldSelectHook
                            fileName="QuestionnaireEditor"
                            fieldName="keyMetricCategory"
                            label="Questionnaire Classification"
                            value={state.keyMetricCategory}
                            onChange={onSelectChangeKeyMetricCategory}
                            width="300px"
                            menuItems={state.keyMetricCategories}
                        />

                        <div style={{width: "20px"}}>&nbsp;</div>

                        {/*// ===========================================================================================================*/}
                        {/*// PORTAL UI TABS */}
                        {/*// ===========================================================================================================*/}

                        {/*<div>Link to tab Styling Tutorial: https://smartdevpreneur.com/the-best-mui-tabs-tutorial-hover-active-focus-and-onclick/</div>*/}
                        {/*<div>Link to tab Styling Tutorial: https://www.youtube.com/watch?v=0IPnrdnagBM</div>*/}

                        <div id="QuestionnaireEditor-tabs-container-div" style={{width: "100%"}}>
                            <Tabs
                                TabIndicatorProps={{
                                    style: {
                                        backgroundColor: "white",
                                        borderColor: "black"
                                    }
                                }}
                                style={{width: "100%"}}
                                sx={{
                                    "& button": {
                                        color: "gray",
                                        border: "solid",
                                        borderWidth: "thin",
                                        width: "20%",
                                        maxWidth: "none"
                                    },
                                    "& button:hover": {
                                        backgroundColor: "#E8E8E8",
                                        color: "black",
                                        borderColor: "black"
                                    },
                                    "& button:active": {backgroundColor: "white", color: "black"},
                                    "& button:Mui-selected": {backgroundColor: "white"},
                                }}
                                value={state.selectedTabNumber ?? 0}
                                onChange={handleTabChange}
                            >
                                <Tab value={0} sx={{
                                    textTransform: "none",
                                    backgroundColor: (state.selectedTabNumber === 0 ? "white" : "#E8E8E8"),
                                    color: (state.selectedTabNumber === 0 ? "black" : "#989898"),
                                }} label={state.tab0}/>

                                <Tab value={1} sx={{
                                    textTransform: "none",
                                    backgroundColor: (state.selectedTabNumber === 1 ? "white" : "#E8E8E8"),
                                    color: (state.selectedTabNumber === 1 ? "black" : "#989898"),
                                }} label={state.tab1}/>

                                <Tab value={2} sx={{
                                    textTransform: "none",
                                    backgroundColor: (state.selectedTabNumber === 2 ? "white" : "#E8E8E8"),
                                    color: (state.selectedTabNumber === 2 ? "black" : "#989898"),
                                }} label={state.tab2}/>

                                <Tab value={3} sx={{
                                    textTransform: "none",
                                    backgroundColor: (state.selectedTabNumber === 3 ? "white" : "#E8E8E8"),
                                    color: (state.selectedTabNumber === 3 ? "black" : "#989898"),
                                }} label={state.tab3}/>

                                <Tab value={4} sx={{
                                    textTransform: "none",
                                    backgroundColor: (state.selectedTabNumber === 4 ? "white" : "#E8E8E8"),
                                    color: (state.selectedTabNumber === 4 ? "black" : "#989898"),
                                }} label={state.tab4}/>

                            </Tabs>

                            {/*// ===========================================================================================================*/}
                            {/*// Tab Sections */}
                            {/*// ===========================================================================================================*/}

                            <div id="QuestionnaireEditor-portal-tab-contents-container-div" style={{
                                paddingBottom: "20px",
                                borderLeft: "solid",
                                borderBottom: "solid",
                                borderRight: "solid",
                                borderWidth: "thin",
                                borderColor: "#1976d2"
                            }}>
                                <div style={{display: state.selectedTabNumber === 0 ? "inline" : "none"}}
                                     id="QuestionnaireEditor-Tab0-container-div">
                                    {getTab0_Header(propsObj)}
                                </div>
                                <div style={{display: state.selectedTabNumber === 1 ? "inline" : "none"}}
                                     id="QuestionnaireEditor-Tab1-container-div">
                                    {getTab_StartOrClosePage("Tab1", "startPage", propsObj)}
                                </div>
                                <div style={{display: state.selectedTabNumber === 2 ? "inline" : "none"}}
                                     id="QuestionnaireEditor-Tab2-container-div">
                                    {getTab2_MainPages(propsObj)}
                                </div>
                                <div style={{display: state.selectedTabNumber === 3 ? "inline" : "none"}}
                                     id="QuestionnaireEditor-Tab3-container-div">
                                    {getTab_StartOrClosePage("Tab3", "closePage", propsObj)}
                                </div>
                                <div style={{display: state.selectedTabNumber === 4 ? "inline" : "none"}}
                                     id="QuestionnaireEditor-Tab4-container-div">
                                    {getTab4_System(propsObj)}
                                </div>
                            </div>
                        </div>

                        {/*// ===========================================================================================================*/}
                        {/*// METADATA */}
                        {/*// ===========================================================================================================*/}

                        {/*<div>&nbsp;</div>*/}

                        <div id="QuestionnaireEditor-metadata-container-div"
                             style={{width: "100%", paddingBottom: "0px"}}>
                            <ArgoMetadataEditorHook
                                entityType={metadataTemplateTypes.QuestionnaireTemplate}
                                template={template}
                                // onChange={updateQuestionnaireTemplate}
                                onChangeCallback={metadataChanged}
                                metadata={template.metadata}
                                errorText={state.metadataErrorText}
                                setMetadataErrorText={setMetadataErrorText}
                                hidden={true}
                            />
                        </div>

                        {/*// ===========================================================================================================*/}
                        {/*// ADD CATEGORY TO QUESTIONNAIRE */}
                        {/*// ===========================================================================================================*/}

                        <div>&nbsp;</div>
                        <div>&nbsp;</div>

                        <div style={{width: "100%"}}>

                            <ArgoSelectExistingHook
                                mode={TEMPLATE.TYPE.CATEGORY}
                                editorName="QuestionnaireEditor"
                                paddingRight="0px"
                                onAddTemplate={addSelectedCategory}
                                width={170}
                                openEditorCallBack={onChangeIsThisAnExistingCategory}
                                createMode={props.createMode}
                                parentTemplate={template}
                            />

                            <div>&nbsp;</div>

                        </div>

                        {/*// ===========================================================================================================*/}
                        {/*// ASSIGNED CATEGORIES LIST */}
                        {/*// ===========================================================================================================*/}

                        <CategoryViewerExpandableHook
                            parentName="QuestionnaireEditor"
                            questionnaireTemplate={template}
                            categoryKeys={categoryKeys}
                            setStateCallback={setStateCallback}
                            hidePortalPreview={true}
                        />

                        {/*// ===========================================================================================================*/}
                        {/*// PORTAL PREVIEW */}
                        {/*// ===========================================================================================================*/}

                        <div id="CategoryEditor-space-above-portal-preview" style={{height: "20px"}}>&nbsp;</div>

                        <ArgoPortalPreviewHook
                            calledBy="QuestionnaireEditor"
                            createMode={props.createMode}
                            templateType={TEMPLATE.TYPE.QUESTIONNAIRE}
                            template={questionnaireTemplateClone}
                            hidePortalPreview={props.cloneMode}
                        />

                    </div>
                </div>
        );
}

QuestionnaireEditor.defaultProps = {
    createMode: false,
    cloneMode: false,
    width: "100%"
};

QuestionnaireEditor.propTypes = {
    createMode: PropTypes.bool,
    templateKey: PropTypes.string,
    handleToolbarButtonClick: PropTypes.func,
    cloneMode: PropTypes.bool,
    width: PropTypes.string
};

export default QuestionnaireEditor;
